import React from 'react';
import {
  Box, Grid, Typography, TextField, Button, Paper, Divider, Chip, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, FormControl, InputLabel,
  Select, MenuItem, CircularProgress, Alert, Stack, Autocomplete, Tooltip,
  Checkbox, FormControlLabel, Accordion, AccordionSummary, AccordionDetails,
  InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Switch, ListItemText,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentIcon from '@mui/icons-material/Payment';
import SplitscreenIcon from '@mui/icons-material/Splitscreen';
import LockIcon from '@mui/icons-material/Lock';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExtensionIcon from '@mui/icons-material/Extension';
import PercentIcon from '@mui/icons-material/Percent';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getRequest, postRequest, patchRequest } from 'Includes/api/apicall';
import { GET_URL, POST_URL, PATCH_URL } from 'Includes/urls';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import axiosAPI from 'Includes/api/api';
import IdCardServiceConfigModal from 'Components/IdCardServiceConfigModal';
import {
  calculateIdCardConfigTotal,
  calculateIdCardConfigTotalUnits,
  listIdCardConfigLineItems,
} from 'Containers/Billing/idCardServiceConfig';

const STATUS_COLORS = {
  draft: { bg: '#f3f4f6', color: '#6b7280' },
  invoice_issued: { bg: '#dbeafe', color: '#2563eb' },
  partially_collected: { bg: '#fef3c7', color: '#d97706' },
  amount_collected: { bg: '#d1fae5', color: '#059669' },
};

const STATUS_LABELS = {
  draft: 'Draft',
  invoice_issued: 'Issued',
  partially_collected: 'Partially Paid',
  amount_collected: 'Paid',
};

const TEAM_STATUS_LABELS = {
  all: 'All clients',
  active: 'Active',
  low_engagement: 'Low Engagement',
  at_risk: 'At Risk',
  dead: 'Dead',
};

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
];

const CHEQUE_STATUSES = [
  { value: 'received', label: 'Received' },
  { value: 'deposited', label: 'Deposited' },
  { value: 'transacted', label: 'Transacted' },
];

const CHEQUE_STATUS_COLORS = {
  received: { bg: '#fef3c7', color: '#b45309' },
  deposited: { bg: '#dbeafe', color: '#1d4ed8' },
  transacted: { bg: '#d1fae5', color: '#047857' },
};

const filterServiceOptions = createFilterOptions({
  stringify: (option) => (typeof option === 'string' ? option : (option?.name || '')),
});

const moneyInputValue = (v) => {
  if (v === null || v === undefined) return '';
  return String(v).replace(/[,₹\s]/g, '');
};

const normalizeServiceItems = (items) => (
  (Array.isArray(items) ? items : []).map((item) => ({
    ...item,
    price: moneyInputValue(item.price),
    count: moneyInputValue(item.count) || item.count || '1',
    gst_percentage: moneyInputValue(item.gst_percentage) || item.gst_percentage || '0',
  }))
);

const parsePrice = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  const n = parseFloat(String(v).replace(/[,₹\s]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const allocationItemIndex = (alloc) => {
  if (!alloc || alloc.item_index === null || alloc.item_index === undefined || alloc.item_index === '') return null;
  const n = Number(alloc.item_index);
  return Number.isFinite(n) ? n : null;
};

const applyServiceItemPayments = (items, payments) => {
  const used = new Set();
  return items.map((item) => {
    if (item.id) return item;
    let paid = 0;
    const wantedLabel = String(item.label || '').trim();
    (payments || []).forEach((pay) => {
      const allocs = Array.isArray(pay.allocations) ? pay.allocations : [];
      allocs.forEach((alloc, allocIdx) => {
        if (!alloc || alloc.installment_id) return;
        const token = `${pay.id}:${allocIdx}`;
        if (used.has(token)) return;
        const amt = parseFloat(alloc.amount) || 0;
        if (amt <= 0) return;
        const idx = allocationItemIndex(alloc);
        const indexMatch = idx !== null && item.itemIndex !== null && item.itemIndex !== undefined && idx === Number(item.itemIndex);
        const labelMatch = idx === null && wantedLabel && String(alloc.label || '').trim() === wantedLabel;
        if (!indexMatch && !labelMatch) return;
        paid += amt;
        used.add(token);
      });
    });
    paid = Math.round(Math.min(item.totalAmount, paid) * 100) / 100;
    const pending = Math.max(0, Math.round((item.totalAmount - paid) * 100) / 100);
    return {
      ...item,
      paidAmount: paid,
      pendingAmount: pending,
      isPaid: item.totalAmount > 0 && paid >= item.totalAmount - 0.009,
    };
  });
};

const stripYearSuffixFromName = (name, yearName) => {
  let text = String(name || '').trim();
  const suffix = yearName ? ` (${yearName})` : '';
  if (suffix && text.endsWith(suffix)) {
    text = text.slice(0, -suffix.length).trim();
  }
  return text;
};

const applyYearSuffixes = (items, options = {}) => {
  const list = Array.isArray(items) ? items : [];
  const yearIds = new Set();
  const yearNames = new Set();
  list.forEach((item) => {
    const yearId = Number(item.academic_year_id);
    if (Number.isFinite(yearId) && yearId > 0) yearIds.add(yearId);
    const yearName = (item.academic_year_name || '').trim();
    if (yearName) yearNames.add(yearName);
  });
  const multiYear = Boolean(options.forceAll) || yearIds.size > 1 || yearNames.size > 1;
  return list.map((item) => {
    const yearName = (item.academic_year_name || '').trim();
    const currentName = (item.name || '').trim();
    const baseSource = (item.base_name || currentName).trim();
    const base = stripYearSuffixFromName(baseSource, yearName) || currentName;
    if (multiYear && yearName && base) {
      return { ...item, base_name: base, name: `${base} (${yearName})` };
    }
    return { ...item, base_name: base, name: base };
  });
};

const isIdCardService = (item) => {
  const base = stripYearSuffixFromName(
    item?.base_name || item?.name || '',
    (item?.academic_year_name || '').trim()
  ).trim().toLowerCase();
  return base === 'id cards' || Boolean(item?.idCardConfig) || Boolean(item?.idCardLines?.length);
};

const yearNameFromList = (yearList, yearId) => {
  const yr = (yearList || []).find((y) => Number(y.id) === Number(yearId));
  return yr ? yr.name : '';
};

const tagServicesWithYear = (items, yearIds, yearList) => {
  const ids = [...new Set((yearIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0))];
  const fallbackYear = ids.length === 1 ? ids[0] : (ids.length ? Math.max(...ids) : null);
  return (Array.isArray(items) ? items : []).map((item) => {
    const yid = Number(item.academic_year_id);
    const yearId = (Number.isFinite(yid) && yid > 0) ? yid : fallbackYear;
    return {
      ...item,
      academic_year_id: yearId || item.academic_year_id,
      academic_year_name: item.academic_year_name || yearNameFromList(yearList, yearId),
      base_name: item.base_name || item.name,
    };
  });
};

const stashServicesByYear = (cache, items, yearIds, yearList) => {
  const next = { ...cache };
  const ids = [...new Set((yearIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0))];
  const tagged = tagServicesWithYear(items, ids, yearList);
  if (ids.length === 1) {
    next[ids[0]] = tagged.map((item) => ({
      ...item,
      academic_year_id: ids[0],
      academic_year_name: item.academic_year_name || yearNameFromList(yearList, ids[0]),
    }));
    return next;
  }
  ids.forEach((id) => {
    next[id] = [];
  });
  tagged.forEach((item) => {
    const yid = Number(item.academic_year_id);
    if (!next[yid]) next[yid] = [];
    next[yid].push(item);
  });
  return next;
};

const composeServicesForYears = (cache, yearIds, yearList) => {
  const ids = [...new Set((yearIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0))];
  const items = [];
  ids.forEach((id) => {
    const rows = Array.isArray(cache[id]) ? cache[id] : [];
    rows.forEach((item) => {
      items.push({
        ...item,
        academic_year_id: id,
        academic_year_name: item.academic_year_name || yearNameFromList(yearList, id),
        base_name: item.base_name || item.name,
      });
    });
  });
  return applyYearSuffixes(items, { forceAll: ids.length > 1 });
};

const seedCacheFromOrderItems = (items, yearIds, yearList) => {
  const ids = [...new Set((yearIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0))];
  const cache = {};
  ids.forEach((id) => { cache[id] = []; });
  (Array.isArray(items) ? items : []).forEach((item) => {
    const yid = Number(item.academic_year_id);
    const yearId = (Number.isFinite(yid) && yid > 0) ? yid : (ids.length === 1 ? ids[0] : ids[0]);
    if (!yearId) return;
    if (!cache[yearId]) cache[yearId] = [];
    cache[yearId].push({
      ...item,
      academic_year_id: yearId,
      academic_year_name: item.academic_year_name || yearNameFromList(yearList, yearId),
      base_name: item.base_name || item.name,
    });
  });
  return cache;
};

const formatAmount = (v) => {
  const n = parsePrice(v);
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const sectionStyle = {
  p: { xs: 2, sm: 2.5 },
  mb: 2.5,
  borderRadius: 2.5,
  border: '1px solid #e2e8f0',
  background: '#fff',
  boxShadow: '0 1px 3px rgba(0,0,0,0.02), 0 2px 8px rgba(0,0,0,0.02)',
};

const sectionTitleStyle = {
  fontWeight: 700,
  fontSize: 15,
  color: '#1e293b',
  mb: 2,
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

function OrdersV2Form(props) {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('id');
  const autoAddPayment = searchParams.get('add_payment');
  const clientStatusFilter = (() => {
    const fromUrl = (searchParams.get('status_by_team') || '').trim().toLowerCase();
    if (fromUrl) return fromUrl;
    try {
      return (sessionStorage.getItem('orders_v2_client_status') || 'all').trim().toLowerCase() || 'all';
    } catch (e) {
      return 'all';
    }
  })();
  const isEdit = Boolean(orderId);
  const navigate = useNavigate();
  const addPaymentTriggeredRef = React.useRef(false);
  const defaultsFetchSeqRef = React.useRef(0);
  const servicesByYearRef = React.useRef({});
  const ordersByYearRef = React.useRef({});

  const [loading, setLoading] = React.useState(isEdit);
  const [saving, setSaving] = React.useState(false);
  const [companies, setCompanies] = React.useState([]);
  const [yearList, setYearList] = React.useState([]);
  const [serviceSuggestions, setServiceSuggestions] = React.useState([]);
  const [addingOtherServiceIdx, setAddingOtherServiceIdx] = React.useState(null);
  const [serviceItems, setServiceItems] = React.useState([]);

  // Order state
  const [order, setOrder] = React.useState({
    title: '',
    description: '',
    company: null,
    academic_year: '',
    academic_years: [],
    notes: '',
  });
  const [orderData, setOrderData] = React.useState(null); // Full order from server
  const [assignedTo, setAssignedTo] = React.useState('');
  const [assigneeUsers, setAssigneeUsers] = React.useState([]);
  const [updatingAssignee, setUpdatingAssignee] = React.useState(false);
  const [installments, setInstallments] = React.useState([]);
  const [payments, setPayments] = React.useState([]);

  // Split dialog
  const [splitCount, setSplitCount] = React.useState('');

  // Payment form
  const [paymentForm, setPaymentForm] = React.useState({
    amount: '',
    payment_date: '',
    method: 'bank_transfer',
    cheque_status: 'received',
    reference: '',
    notes: '',
  });
  const [sideItemPayments, setSideItemPayments] = React.useState({});
  const [paymentSubmitting, setPaymentSubmitting] = React.useState(false);
  const [deletingPaymentId, setDeletingPaymentId] = React.useState(null);
  const [updatingChequeStatusId, setUpdatingChequeStatusId] = React.useState(null);

  // Dedicated Add Amount modal & Print Invoice state
  const [addAmountModalOpen, setAddAmountModalOpen] = React.useState(false);
  const [modalPayment, setModalPayment] = React.useState({
    amount: '',
    payment_date: '',
    method: 'bank_transfer',
    cheque_status: 'received',
    reference: '',
    notes: '',
  });
  const [modalPaymentSubmitting, setModalPaymentSubmitting] = React.useState(false);
  const [itemPayments, setItemPayments] = React.useState({});
  const [printingInvoice, setPrintingInvoice] = React.useState(false);
  const [printingPending, setPrintingPending] = React.useState(false);
  const [invoiceStatus, setInvoiceStatus] = React.useState('draft');
  const [selectedInstallmentKeys, setSelectedInstallmentKeys] = React.useState([]);

  // ID Card configuration modal
  const [idCardModalOpen, setIdCardModalOpen] = React.useState(false);
  const [idCardEditIndex, setIdCardEditIndex] = React.useState(null);
  const [idCardInitialConfig, setIdCardInitialConfig] = React.useState(null);

  const selectedYearIds = () => {
    const years = (order.academic_years && order.academic_years.length > 0)
      ? order.academic_years
      : (order.academic_year ? [order.academic_year] : []);
    return [...new Set(years.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0))];
  };

  const rememberOrder = (saved, yearIdHint) => {
    if (!saved || !saved.id) return;
    const m2m = (Array.isArray(saved.academic_years) ? saved.academic_years : [])
      .map((y) => Number(y && y.id != null ? y.id : y))
      .filter((n) => Number.isFinite(n) && n > 0);
    const fk = Number(saved.academic_year && saved.academic_year.id != null
      ? saved.academic_year.id
      : saved.academic_year);
    const hinted = Number(yearIdHint);
    const yearId = (Number.isFinite(hinted) && hinted > 0 && hinted)
      || (m2m.length === 1 ? m2m[0] : 0)
      || (Number.isFinite(fk) && fk > 0 ? fk : 0)
      || m2m[0];
    if (!yearId) return;
    ordersByYearRef.current[yearId] = saved;
  };

  const ingestOrdersByYear = (byYear, overwriteServices = false) => {
    if (!byYear || typeof byYear !== 'object') return;
    Object.entries(byYear).forEach(([yearKey, payload]) => {
      const yearId = Number(yearKey);
      if (!payload || !payload.id || !yearId) return;
      ordersByYearRef.current[yearId] = payload;
      if (!Array.isArray(payload.opted_features)) return;
      if (!overwriteServices && servicesByYearRef.current[yearId] !== undefined) return;
      const seeded = seedCacheFromOrderItems(payload.opted_features, [yearId], yearList);
      servicesByYearRef.current[yearId] = seeded[yearId] || [];
    });
  };

  const applyOrdersForYears = (yearIds) => {
    const ids = (yearIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
    const cachedOrders = ids.map((id) => ordersByYearRef.current[id]).filter((row) => row && row.id);
    if (ids.length === 1) {
      const cached = ordersByYearRef.current[ids[0]];
      if (cached && cached.id) {
        setOrderData(cached);
        setInstallments(cached.installments || []);
        setPayments(cached.payments || []);
        setInvoiceStatus(cached.status || 'draft');
        return;
      }
      setOrderData(null);
      setInstallments([]);
      setPayments([]);
      setInvoiceStatus('draft');
      return;
    }
    if (!cachedOrders.length) {
      setOrderData(null);
      setInstallments([]);
      setPayments([]);
      setInvoiceStatus('draft');
      return;
    }
    const received = cachedOrders.reduce((sum, row) => sum + (parseFloat(row.received_amount) || 0), 0);
    const invoices = cachedOrders.map((row) => row.invoice_number).filter(Boolean);
    const primary = cachedOrders[cachedOrders.length - 1] || null;
    setOrderData({
      ...(primary || {}),
      received_amount: received,
      invoice_number: invoices.join(' / '),
    });
    setInstallments(cachedOrders.flatMap((row) => row.installments || []));
    setPayments(cachedOrders.flatMap((row) => row.payments || []));
    if (primary?.status) setInvoiceStatus(primary.status);
  };

  const getActiveOrderId = () => {
    const years = selectedYearIds();
    if (years.length === 1) {
      const cached = ordersByYearRef.current[years[0]];
      if (cached?.id) return cached.id;
      return null;
    }
    if (years.length > 1) {
      const last = ordersByYearRef.current[years[years.length - 1]];
      return last?.id || null;
    }
    return null;
  };

  const bindServerOrder = (payload) => {
    if (!payload || !payload.id) return;
    rememberOrder(payload);
    const years = selectedYearIds();
    if (years.length === 1) {
      applyOrdersForYears(years);
      return;
    }
    setOrderData(payload);
    setInstallments(payload.installments || []);
    setPayments(payload.payments || []);
    if (payload.status) setInvoiceStatus(payload.status);
  };

  React.useEffect(() => {
    fetchCompanies();
    fetchYears();
    fetchServiceSuggestions();
  }, [clientStatusFilter]);

  React.useEffect(() => {
    getRequest(GET_URL.user.api, { status: 'active' }).then((response) => {
      if (response && response.status === 200) {
        const list = response.data?.data || response.data || [];
        setAssigneeUsers(Array.isArray(list) ? list : []);
      }
    });
  }, []);

  React.useEffect(() => {
    if (!orderData) return;
    setAssignedTo(orderData.assigned_to ? String(orderData.assigned_to) : '');
  }, [orderData]);

  React.useEffect(() => {
    if (orderId) {
      fetchOrder(orderId);
    } else {
      const urlParams = new URLSearchParams(window.location.search);
      const qYear = urlParams.get('year');
      const qCompany = urlParams.get('company');
      const parsedQYear = qYear ? parseInt(qYear, 10) : null;
      const parsedQCompany = qCompany ? parseInt(qCompany, 10) : null;
      const defaultYr = parsedQYear || (yearList.length > 0 ? (yearList.find((y) => y.is_active || y.active)?.id || yearList[0]?.id) : '');
      servicesByYearRef.current = {};
      ordersByYearRef.current = {};
      setOrder({
        title: '',
        description: '',
        company: Number.isFinite(parsedQCompany) && parsedQCompany > 0 ? parsedQCompany : null,
        academic_year: defaultYr || '',
        academic_years: defaultYr ? [defaultYr] : [],
        notes: '',
      });
      setServiceItems([]);
      setInstallments([]);
      setPayments([]);
      setInvoiceStatus('draft');
      setLoading(false);
    }
  }, [orderId]);

  React.useEffect(() => {
    const companyId = order.company;
    if (!companyId || !yearList.length) return undefined;
    const missing = yearList
      .map((y) => Number(y.id))
      .filter((id) => Number.isFinite(id) && id > 0 && !ordersByYearRef.current[id]);
    if (!missing.length) return undefined;
    let cancelled = false;
    getRequest(
      GET_URL.orders_v2.api + `company_defaults/?company_id=${companyId}&academic_years=${missing.join(',')}`
    ).then((res) => {
      if (cancelled || !res || res.status !== 200 || !res.data) return;
      ingestOrdersByYear(res.data.orders_by_year, false);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [order.company, yearList]);

  const fetchServiceSuggestions = () => {
    const defaultServices = [
      { name: 'ERP', price: '', calculation_type: 'studentcount' },
      { name: 'Website', price: '', calculation_type: 'yearwise' },
      { name: 'Banner', price: '', calculation_type: 'yearwise' },
      { name: 'Biometric', price: '', calculation_type: 'yearwise' },
      { name: 'ID Cards', price: '', calculation_type: 'studentcount' },
    ];
    if (GET_URL.digitalmarketingfeature?.api) {
      getRequest(GET_URL.digitalmarketingfeature.api, { limit: 500, pageno: 1 }).then((response) => {
        if (response && response.status === 200) {
          const data = response.data.data || response.data || [];
          const apiServices = (Array.isArray(data) ? data : []).map((s) => ({
            name: s.name || '',
            price: s.price || '',
            calculation_type: s.calculation_type || 'monthwise',
          }));
          // Merge: API services first, then defaults (skip duplicates)
          const seen = new Set(apiServices.map((s) => s.name.toLowerCase()));
          const merged = [...apiServices];
          defaultServices.forEach((ds) => {
            if (!seen.has(ds.name.toLowerCase())) merged.push(ds);
          });
          setServiceSuggestions(merged);
        } else {
          setServiceSuggestions(defaultServices);
        }
      }).catch(() => setServiceSuggestions(defaultServices));
    } else {
      setServiceSuggestions(defaultServices);
    }
  };

  const fetchCompanies = () => {
    const params = { limit: 9999, pageno: 1 };
    if (clientStatusFilter && clientStatusFilter !== 'all') {
      params.status_by_team = clientStatusFilter;
    }
    getRequest(GET_URL.company.api, params).then((response) => {
      if (response && response.status === 200) {
        const data = response.data.data || response.data || [];
        const list = Array.isArray(data) ? data : [];
        list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));
        setCompanies(list);
      }
    });
  };

  const fetchYears = () => {
    getRequest(GET_URL.academicyear.api).then((response) => {
      if (response && response.status === 200) {
        const years = response.data.data || response.data || [];
        const arr = Array.isArray(years) ? years : [];
        setYearList(arr);
        const urlParams = new URLSearchParams(window.location.search);
        const qYear = urlParams.get('year');
        const parsedQYear = qYear ? parseInt(qYear, 10) : null;
        setOrder((prev) => {
          if (orderId || prev.academic_year || (prev.academic_years && prev.academic_years.length > 0) || arr.length === 0) {
            return prev;
          }
          const activeYr = (parsedQYear && arr.some((y) => Number(y.id) === parsedQYear))
            ? parsedQYear
            : (arr.find((y) => y.is_active || y.active)?.id || arr[0]?.id);
          return {
            ...prev,
            academic_year: activeYr || '',
            academic_years: activeYr ? [Number(activeYr)] : [],
          };
        });
      }
    });
  };

  const fetchOrder = (id = orderId) => {
    if (!id) return;
    setLoading(true);
    getRequest(GET_URL.orders_v2.api + id + '/').then((response) => {
      if (response && response.status === 200) {
        const d = response.data;
        setOrderData(d);
        setInvoiceStatus(d.status || 'draft');
        rememberOrder(d);
        const loadedYears = Array.isArray(d.academic_years) && d.academic_years.length > 0
          ? d.academic_years.map((y) => Number(y && y.id != null ? y.id : y)).filter((n) => Number.isFinite(n) && n > 0)
          : (d.academic_year ? [Number(d.academic_year && d.academic_year.id != null ? d.academic_year.id : d.academic_year)] : []);
        setOrder({
          title: d.title || '',
          description: d.description || '',
          company: d.company || null,
          academic_year: d.academic_year || '',
          academic_years: loadedYears,
          notes: d.notes || '',
        });
        // Load service items from opted_features
        const loadedItems = Array.isArray(d.opted_features) ? d.opted_features : [];
        const yearIdsForCache = loadedYears.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
        servicesByYearRef.current = seedCacheFromOrderItems(loadedItems, yearIdsForCache, yearList);
        if (loadedItems.length > 0 && typeof loadedItems[0] === 'object') {
          setServiceItems(normalizeServiceItems(applyYearSuffixes(
            composeServicesForYears(servicesByYearRef.current, yearIdsForCache, yearList)
          )));
        } else {
          setServiceItems([]);
        }
        setInstallments(d.installments || []);
        setPayments(d.payments || []);
        applyOrdersForYears(yearIdsForCache);

        if (d.company) {
          const siblingYears = yearList.map((y) => Number(y.id)).filter((id) => Number.isFinite(id) && id > 0 && !ordersByYearRef.current[id]);
          if (siblingYears.length) {
            getRequest(
              GET_URL.orders_v2.api + `company_defaults/?company_id=${d.company}&academic_years=${siblingYears.join(',')}`
            ).then((res) => {
              if (res && res.status === 200 && res.data) {
                ingestOrdersByYear(res.data.orders_by_year, false);
              }
            }).catch(() => {});
          }
        }

        if (autoAddPayment === '1' && !addPaymentTriggeredRef.current) {
          addPaymentTriggeredRef.current = true;
          const remaining = (parseFloat(d.total_amount) || 0) - (parseFloat(d.received_amount) || 0);
          setModalPayment({
            amount: remaining > 0 ? remaining.toFixed(2) : '',
            payment_date: new Date().toISOString().slice(0, 16),
            method: 'bank_transfer',
            cheque_status: 'received',
            reference: '',
            notes: '',
          });
          setAddAmountModalOpen(true);
        }
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  // Computed values from service items
  const { computedSubtotal, computedGst, computedTotal, splittableTotal, excludedTotal, excludedItemsList } = React.useMemo(() => {
    let subtotal = 0;
    let gstTotal = 0;
    let splittable = 0;
    let excluded = 0;
    const excludedList = [];
    serviceItems.forEach((item, idx) => {
      const price = parsePrice(item.price);
      const count = parsePrice(item.count) || 1;
      const isFixed = Boolean(item.is_fixed || item.amount_fixed || item.erpAmountFixed);
      const lineBase = isFixed ? price : (price * count);
      let lineGst = 0;
      if (item.has_gst) {
        const gstPct = parseFloat(item.gst_percentage) || 0;
        lineGst = (lineBase * gstPct / 100);
      }
      const lineTotal = lineBase + lineGst;
      subtotal += lineBase;
      gstTotal += lineGst;
      if (item.exclude_from_split) {
        excluded += lineTotal;
        excludedList.push({
          ...item,
          originalIndex: idx,
          lineTotal: Math.round(lineTotal * 100) / 100,
        });
      } else {
        splittable += lineTotal;
      }
    });
    return {
      computedSubtotal: Math.round(subtotal * 100) / 100,
      computedGst: Math.round(gstTotal * 100) / 100,
      computedTotal: Math.round((subtotal + gstTotal) * 100) / 100,
      splittableTotal: Math.round(splittable * 100) / 100,
      excludedTotal: Math.round(excluded * 100) / 100,
      excludedItemsList: excludedList,
    };
  }, [serviceItems]);
  const receivedAmount = orderData ? parseFloat(orderData.received_amount) : 0;
  const balanceAmount = Math.max(0, computedTotal - receivedAmount);
  const installmentCollected = (installments || []).reduce(
    (sum, inst) => sum + (parseFloat(inst.paid_amount) || 0),
    0,
  );
  // When services are opted out, only the remaining split services form the pool (can be 0).
  const splitBase = excludedTotal > 0 ? splittableTotal : (splittableTotal > 0 ? splittableTotal : computedTotal);
  const isEmptyInstallment = React.useCallback((inst) => (
    parseFloat(inst?.amount || 0) <= 0
    && parseFloat(inst?.paid_amount || 0) <= 0
    && (Boolean(inst?.id) || splitBase <= 0)
  ), [splitBase]);
  // Payments on non-split items are not part of the installment pool.
  const remainingSplittableBalance = Math.max(0, splitBase - installmentCollected);
  const targetSplitPool = installmentCollected > 0 ? remainingSplittableBalance : splitBase;

  const payableInstallmentItems = React.useMemo(() => {
    const items = [];
    if (Array.isArray(installments) && installments.length > 0) {
      installments.forEach((inst, i) => {
        if (isEmptyInstallment(inst)) return;
        const total = parseFloat(inst.amount || 0);
        const paid = parseFloat(inst.paid_amount || 0);
        const pending = Math.max(0, total - paid);
        items.push({
          key: `inst-${inst.id || i}`,
          id: inst.id,
          label: inst.label || `Installment ${inst.installment_number || (i + 1)}`,
          totalAmount: total,
          paidAmount: paid,
          pendingAmount: pending,
          isPaid: inst.is_paid || (total > 0 && paid >= total),
        });
      });
    }
    if (Array.isArray(excludedItemsList) && excludedItemsList.length > 0) {
      excludedItemsList.forEach((ex, i) => {
        const total = parseFloat(ex.lineTotal || 0);
        items.push({
          key: `ex-${ex.originalIndex ?? i}`,
          id: null,
          itemIndex: ex.originalIndex ?? i,
          label: `${ex.name || 'Service'} (Non-split)`,
          totalAmount: total,
          paidAmount: 0,
          pendingAmount: total,
          isPaid: false,
        });
      });
    } else if (items.length === 0 && Array.isArray(serviceItems) && serviceItems.length > 0) {
      serviceItems.forEach((it, i) => {
        const isFixed = Boolean(it.is_fixed || it.amount_fixed || it.erpAmountFixed);
        const price = parsePrice(it.price);
        const count = parsePrice(it.count) || 1;
        const lineBase = isFixed ? price : price * count;
        let gstAmt = 0;
        if (it.has_gst) {
          gstAmt = lineBase * (parseFloat(it.gst_percentage) || 0) / 100;
        }
        const total = Math.round((lineBase + gstAmt) * 100) / 100;
        items.push({
          key: `svc-${i}`,
          id: null,
          itemIndex: i,
          label: it.name || `Service #${i + 1}`,
          totalAmount: total,
          paidAmount: 0,
          pendingAmount: total,
          isPaid: false,
        });
      });
    }
    return applyServiceItemPayments(items, payments);
  }, [installments, excludedItemsList, serviceItems, payments, isEmptyInstallment]);

  const selectablePayableItems = React.useMemo(() => {
    return payableInstallmentItems.filter((it) => !it.isPaid && it.pendingAmount > 0);
  }, [payableInstallmentItems]);
  const currentStatus = invoiceStatus || orderData?.status || 'draft';
  const invoiceNumber = orderData?.invoice_number || '';
  const invoiceDate = orderData?.invoice_date || '';

  const handleStatusChange = async (newStatus) => {
    if (newStatus === currentStatus) return;
    const previousStatus = currentStatus;
    setInvoiceStatus(newStatus);
    const targetId = getActiveOrderId();
    if (!targetId) {
      return;
    }
    try {
      const response = await postRequest(GET_URL.orders_v2.api + targetId + '/set_status/', { status: newStatus });
      if (response && (response.status === 200 || response.status === 201)) {
        bindServerOrder(response.data);
        setInvoiceStatus(response.data.status || newStatus);
        Swal.fire({
          icon: 'success',
          title: `Status set to ${STATUS_LABELS[newStatus] || newStatus}`,
          timer: 1200,
          showConfirmButton: false,
        });
      } else {
        setInvoiceStatus(previousStatus);
        Swal.fire({ icon: 'error', title: 'Could not update status' });
      }
    } catch (err) {
      setInvoiceStatus(previousStatus);
      console.error('Failed to update status:', err);
      Swal.fire({ icon: 'error', title: 'Could not update status' });
    }
  };

  const handleCompanyChange = async (val) => {
    if (!val) {
      setOrder((prev) => ({ ...prev, company: null }));
      return;
    }

    const companyId = val.id;
    const targetAy = order.academic_year || (yearList.find((y) => y.is_active || y.active)?.id || yearList[0]?.id);
    const currentYears = (order.academic_years && order.academic_years.length > 0)
      ? order.academic_years.map((id) => Number(id))
      : (order.academic_year ? [Number(order.academic_year)] : (targetAy ? [Number(targetAy)] : []));

    servicesByYearRef.current = {};
    ordersByYearRef.current = {};

    setOrder((prev) => ({
      ...prev,
      company: companyId,
      title: `Order - ${val.name || ''}`,
      academic_year: currentYears.length ? Math.max(...currentYears) : (prev.academic_year || ''),
      academic_years: currentYears,
    }));

    const seq = ++defaultsFetchSeqRef.current;
    try {
      const allYearIds = yearList.map((y) => Number(y.id)).filter((id) => Number.isFinite(id) && id > 0);
      const fetchYears = allYearIds.length ? allYearIds : currentYears;
      const yearQuery = fetchYears.length > 0 ? `&academic_years=${fetchYears.join(',')}` : (targetAy ? `&academic_year=${targetAy}` : '');
      const res = await getRequest(
        GET_URL.orders_v2.api + `company_defaults/?company_id=${companyId}${yearQuery}`
      );
      if (seq !== defaultsFetchSeqRef.current) return;
      if (res && res.status === 200 && res.data) {
        const d = res.data;
        setOrder((prev) => ({
          ...prev,
          company: companyId,
          title: fetchYears.length === currentYears.length
            ? (d.title || `Order - ${val.name || ''}`)
            : (prev.title || `Order - ${val.name || ''}`),
          academic_year: currentYears.length ? Math.max(...currentYears) : (d.academic_year || prev.academic_year || targetAy || ''),
          academic_years: currentYears.length ? currentYears : prev.academic_years,
          notes: d.notes !== undefined && d.notes !== null ? d.notes : (prev.notes || ''),
        }));

        ingestOrdersByYear(d.orders_by_year, true);
        const billingItems = applyYearSuffixes(Array.isArray(d.opted_features) ? d.opted_features : []);
        const billingCache = seedCacheFromOrderItems(billingItems, fetchYears.length ? fetchYears : currentYears, yearList);
        Object.keys(billingCache).forEach((key) => {
          const yearId = Number(key);
          if (servicesByYearRef.current[yearId] === undefined) {
            servicesByYearRef.current[yearId] = billingCache[yearId] || [];
          }
        });
        setServiceItems(normalizeServiceItems(composeServicesForYears(servicesByYearRef.current, currentYears, yearList)));
        applyOrdersForYears(currentYears);
      }
    } catch (err) {
      console.error('Failed to auto-fetch company defaults:', err);
    }
  };

  const sortedCompanies = React.useMemo(() => {
    const list = Array.isArray(companies) ? [...companies] : [];
    list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));
    return list;
  }, [companies]);

  const currentCompanyIndex = sortedCompanies.findIndex((c) => Number(c.id) === Number(order.company));

  const moveClient = (delta) => {
    if (!sortedCompanies.length) return;
    const idx = currentCompanyIndex;
    const nextIdx = idx < 0
      ? (delta > 0 ? 0 : sortedCompanies.length - 1)
      : ((idx + delta + sortedCompanies.length) % sortedCompanies.length);
    const nextCompany = sortedCompanies[nextIdx];
    if (nextCompany) handleCompanyChange(nextCompany);
  };

  const handleAcademicYearsChange = async (selectedVal) => {
    const raw = Array.isArray(selectedVal)
      ? selectedVal
      : (typeof selectedVal === 'string' ? selectedVal.split(',').filter(Boolean) : [selectedVal]);
    const newYearIds = [...new Set(raw.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0))];
    const primaryYear = newYearIds.length > 0 ? Math.max(...newYearIds) : '';
    const prevYearIds = (order.academic_years && order.academic_years.length > 0
      ? order.academic_years
      : (order.academic_year ? [order.academic_year] : [])
    ).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);

    servicesByYearRef.current = stashServicesByYear(
      servicesByYearRef.current,
      serviceItems,
      prevYearIds,
      yearList
    );

    let companyId = null;
    setOrder((prev) => {
      companyId = prev.company;
      return {
        ...prev,
        academic_years: newYearIds,
        academic_year: primaryYear,
      };
    });

    if (newYearIds.length === 0) {
      setServiceItems([]);
      applyOrdersForYears([]);
      return;
    }

    const missingYears = newYearIds.filter((id) => servicesByYearRef.current[id] === undefined);
    const yearsNeedingOrder = newYearIds.filter((id) => !ordersByYearRef.current[id]);
    const yearsToFetch = [...new Set([...missingYears, ...yearsNeedingOrder])];
    if (companyId && yearsToFetch.length > 0) {
      const seq = ++defaultsFetchSeqRef.current;
      try {
        const yearParam = `&academic_years=${yearsToFetch.join(',')}`;
        const res = await getRequest(
          GET_URL.orders_v2.api + `company_defaults/?company_id=${companyId}${yearParam}`
        );
        if (seq === defaultsFetchSeqRef.current && res && res.status === 200 && res.data) {
          ingestOrdersByYear(res.data.orders_by_year, false);
          const incoming = applyYearSuffixes(Array.isArray(res.data.opted_features) ? res.data.opted_features : []);
          const fetchedCache = seedCacheFromOrderItems(incoming, yearsToFetch, yearList);
          yearsToFetch.forEach((id) => {
            if (servicesByYearRef.current[id] === undefined) {
              servicesByYearRef.current[id] = fetchedCache[id] || [];
            }
          });
          setOrder((prev) => ({
            ...prev,
            academic_years: newYearIds,
            academic_year: primaryYear,
            title: res.data.title || prev.title,
          }));
        }
      } catch (err) {
        console.error('Failed to load defaults for academic year:', err);
      }
    }

    missingYears.forEach((id) => {
      if (servicesByYearRef.current[id] === undefined) {
        servicesByYearRef.current[id] = [];
      }
    });
    setServiceItems(normalizeServiceItems(composeServicesForYears(servicesByYearRef.current, newYearIds, yearList)));
    applyOrdersForYears(newYearIds);
  };

  // Save order
  const handleSave = async (silent = false) => {
    if (!order.company) {
      Swal.fire({
        icon: 'warning',
        title: 'Client Required',
        text: 'Please select a Client / School for this order.',
      });
      return;
    }
    const activeYears = (order.academic_years && order.academic_years.length > 0
      ? order.academic_years
      : (order.academic_year ? [order.academic_year] : [])
    ).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
    const primaryYear = activeYears.length > 0 ? Math.max(...activeYears) : (order.academic_year || null);

    if (activeYears.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Academic Year Required',
        text: 'Please select at least one Academic Year for this order.',
      });
      return;
    }
    servicesByYearRef.current = stashServicesByYear(
      servicesByYearRef.current,
      serviceItems,
      activeYears,
      yearList
    );
    const yearsPayload = activeYears.map((yearId) => ({
      academic_year: yearId,
      opted_features: tagServicesWithYear(
        servicesByYearRef.current[yearId] || [],
        [yearId],
        yearList
      ),
    }));
    setSaving(true);
    const payload = {
      title: order.title,
      description: order.description,
      company: order.company || null,
      academic_year: primaryYear,
      academic_years: activeYears,
      notes: order.notes,
      status: invoiceStatus || currentStatus,
      years: yearsPayload,
    };

    try {
      const currentTargetId = getActiveOrderId();
      const response = await postRequest(
        POST_URL.orders_v2.api + 'save_years/',
        payload,
        { return_error: true }
      );

      if (response && (response.status === 200 || response.status === 201)) {
        const body = response.data || {};
        const d = body.data || body;
        const savedOrders = Array.isArray(body.orders) && body.orders.length ? body.orders : [d];
        savedOrders.forEach((saved) => {
          rememberOrder(saved);
          const yearId = Number(saved.academic_year);
          if (!yearId) return;
          const yearItems = Array.isArray(saved.opted_features) ? saved.opted_features : [];
          servicesByYearRef.current[yearId] = seedCacheFromOrderItems(yearItems, [yearId], yearList)[yearId] || [];
        });
        setOrder({
          title: d.title || '',
          description: d.description || '',
          company: d.company || order.company,
          academic_year: primaryYear,
          academic_years: activeYears,
          notes: d.notes || '',
        });
        setServiceItems(normalizeServiceItems(
          composeServicesForYears(servicesByYearRef.current, activeYears, yearList)
        ));
        applyOrdersForYears(activeYears);

        if (!silent) {
          Swal.fire({
            icon: 'success',
            title: currentTargetId ? 'Order Updated' : 'Order Created',
            text: `Order ${d.order_number} saved successfully`,
            timer: 2000,
            showConfirmButton: false,
          });
        }

        const unpaidRows = (installments || []).filter((inst) => !inst.is_paid && !(parseFloat(inst.paid_amount || 0) > 0));
        if (!currentTargetId && unpaidRows.length > 0 && d.id) {
          try {
            const instResp = await postRequest(
              POST_URL.orders_v2.api + d.id + '/installments/',
              { installments: unpaidRows }
            );
            if (instResp && instResp.status === 200) {
              setInstallments(instResp.data.installments || []);
            }
          } catch (e) {
            console.error('Failed to auto-save installments:', e);
          }
        }

        if (!currentTargetId) {
          navigate(`/billing/orders-v2/form?id=${d.id}`, { replace: true });
        }
        setSaving(false);
        return { ...d, orders: savedOrders };
      } else {
        const errText = response?.data?.error || 'Failed to save order';
        Swal.fire({ icon: 'error', title: 'Cannot save order', text: errText });
      }
    } catch (err) {
      const errText = err?.response?.data?.error || err?.data?.error || 'Failed to save order';
      Swal.fire({ icon: 'error', title: 'Cannot save order', text: errText });
    }
    setSaving(false);
    return null;
  };

  // Service item handlers
  const addServiceItem = (isFixed = false) => {
    const yearIds = (order.academic_years && order.academic_years.length > 0
      ? order.academic_years
      : (order.academic_year ? [order.academic_year] : [])).map((id) => Number(id));
    const yearId = yearIds.length === 1 ? yearIds[0] : (yearIds.length ? Math.max(...yearIds) : null);
    setServiceItems([...serviceItems, {
      name: '',
      price: '',
      count: '1',
      is_fixed: Boolean(isFixed),
      has_gst: false,
      gst_percentage: '18',
      exclude_from_split: false,
      print_note: '',
      academic_year_id: yearId,
      academic_year_name: yearNameFromList(yearList, yearId),
    }]);
  };

  const updateServiceItem = (index, field, value) => {
    setServiceItems((prev) => {
      const updated = [...prev];
      const item = { ...updated[index], [field]: value };
      if (field === 'is_fixed' && value) {
        item.amount_fixed = true;
      }
      if (field === 'is_fixed' && !value) {
        item.amount_fixed = false;
        item.erpAmountFixed = false;
      }
      updated[index] = item;
      return updated;
    });
  };

  const removeServiceItem = (index) => {
    setServiceItems(serviceItems.filter((_, i) => i !== index));
  };

  const openIdCardConfigModal = (index) => {
    setIdCardEditIndex(index);
    setIdCardInitialConfig(serviceItems[index]?.idCardConfig || null);
    setIdCardModalOpen(true);
  };

  const handleServiceSelect = (index, selectedName) => {
    const suggestion = serviceSuggestions.find((s) => s.name === selectedName);

    // If ID Cards selected, open the config modal
    if (selectedName && selectedName.toLowerCase() === 'id cards') {
      const existingItem = serviceItems[index];
      setIdCardEditIndex(index);
      setIdCardInitialConfig(existingItem?.idCardConfig || null);
      const updated = [...serviceItems];
      updated[index] = {
        ...updated[index],
        name: selectedName,
        base_name: 'ID Cards',
        has_gst: false,
      };
      setServiceItems(applyYearSuffixes(updated, { forceAll: selectedYearIds().length > 1 }));
      setIdCardModalOpen(true);
      return;
    }

    const updated = [...serviceItems];
    updated[index] = {
      ...updated[index],
      name: selectedName || '',
      base_name: selectedName || '',
      price: suggestion?.price || updated[index].price || '',
    };
    setServiceItems(applyYearSuffixes(updated, { forceAll: selectedYearIds().length > 1 }));
  };

  const serviceNameIsKnown = (name) => {
    const key = String(name || '').trim().toLowerCase();
    if (!key) return true;
    return serviceSuggestions.some((s) => String(s.name || '').trim().toLowerCase() === key);
  };

  const handleAddOtherService = async (index, rawName) => {
    const name = String(rawName || serviceItems[index]?.name || '').trim();
    if (!name || addingOtherServiceIdx !== null) return;
    if (serviceNameIsKnown(name)) {
      handleServiceSelect(index, name);
      return;
    }
    if (!POST_URL.digitalmarketingfeature?.api) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Other Services is not configured.' });
      return;
    }
    setAddingOtherServiceIdx(index);
    try {
      const priceNum = parsePrice(serviceItems[index]?.price);
      const response = await postRequest(
        POST_URL.digitalmarketingfeature.api,
        [{
          name,
          price: priceNum || null,
          calculation_type: 'yearwise',
          terms_and_condition: [],
        }],
        { return_error: true }
      );
      if (!response || (response.status !== 200 && response.status !== 201)) {
        const msg = response?.data?.message || response?.data?.detail || 'Failed to add other service';
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: typeof msg === 'string' ? msg : 'Failed to add other service',
        });
        return;
      }
      const created = response.data?.data;
      const createdRow = Array.isArray(created) ? created[0] : created;
      const savedName = (createdRow?.name || name).trim();
      setServiceSuggestions((prev) => {
        if (prev.some((s) => String(s.name || '').trim().toLowerCase() === savedName.toLowerCase())) return prev;
        return [...prev, {
          name: savedName,
          price: createdRow?.price ?? '',
          calculation_type: createdRow?.calculation_type || 'yearwise',
        }];
      });
      handleServiceSelect(index, savedName);
      Swal.fire({
        icon: 'success',
        title: 'Added to Other Services',
        text: savedName,
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to add other service' });
    } finally {
      setAddingOtherServiceIdx(null);
    }
  };

  const handleIdCardConfigConfirm = (config) => {
    if (idCardEditIndex === null) return;
    const total = calculateIdCardConfigTotal(config);
    const units = calculateIdCardConfigTotalUnits(config);
    const lines = listIdCardConfigLineItems(config);

    const updated = [...serviceItems];
    let price = '';
    let count = 1;

    if (lines.length === 1) {
      count = lines[0].count || 1;
      price = String(lines[0].unitPrice || 0);
    } else if (total > 0) {
      count = 1;
      price = total.toFixed(2);
    }

    updated[idCardEditIndex] = {
      ...updated[idCardEditIndex],
      name: 'ID Cards',
      base_name: 'ID Cards',
      price,
      count,
      has_gst: false,
      idCardConfig: config,
      idCardLines: lines,
    };
    setServiceItems(applyYearSuffixes(updated, { forceAll: selectedYearIds().length > 1 }));
    setIdCardModalOpen(false);
    setIdCardEditIndex(null);
    setIdCardInitialConfig(null);
  };

  // Before any installment is collected, keep unpaid rows matched to the splittable total.
  // After a collection, percentages describe the remaining balance, so do not rebuild amounts.
  React.useEffect(() => {
    if (installmentCollected > 0 || installments.length === 0 || targetSplitPool < 0) return;
    setInstallments((prev) =>
      prev.map((inst) => {
        if (inst.is_paid || parseFloat(inst.paid_amount || 0) > 0) return inst;
        if (targetSplitPool === 0) return { ...inst, amount: '0.00' };
        if (inst.percentage === '' || isNaN(parseFloat(inst.percentage))) return inst;
        return {
          ...inst,
          amount: ((targetSplitPool * parseFloat(inst.percentage)) / 100).toFixed(2),
        };
      })
    );
  }, [targetSplitPool, installmentCollected]);

  // Split installments
  const handleSplitInstallments = async () => {
    const count = parseInt(splitCount);
    if (!count || count < 1) {
      Swal.fire({ icon: 'warning', title: 'Invalid', text: 'Enter a valid split count' });
      return;
    }

    const paidInstallments = installments.filter((inst) => parseFloat(inst.paid_amount || 0) > 0);
    const startNum = paidInstallments.length > 0
      ? (Math.max(...paidInstallments.map((p) => parseInt(p.installment_number, 10) || 0)) + 1)
      : 1;

    const targetSplitAmount = targetSplitPool;
    const perPct = (100 / count).toFixed(2);
    const rows = [];
    let sumPct = 0;
    let sumAmt = 0;
    for (let i = 0; i < count; i++) {
      const isLast = i === count - 1;
      const pct = isLast ? (100 - sumPct).toFixed(2) : perPct;
      sumPct += parseFloat(pct);
      const amt = isLast ? (targetSplitAmount - sumAmt).toFixed(2) : ((targetSplitAmount * parseFloat(pct)) / 100).toFixed(2);
      sumAmt += parseFloat(amt);
      rows.push({
        label: `Installment ${startNum + i}`,
        percentage: pct,
        amount: amt,
        due_date: '',
        paid_amount: '0.00',
        is_paid: false,
      });
    }

    const currentTargetId = getActiveOrderId();
    if (currentTargetId) {
      try {
        const response = await postRequest(
          POST_URL.orders_v2.api + currentTargetId + '/installments/',
          { installments: rows }
        );
        if (response && response.status === 200) {
          bindServerOrder(response.data);
          setSplitCount('');
          Swal.fire({ icon: 'success', title: 'Split Done', timer: 1500, showConfirmButton: false });
        }
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to split installments' });
      }
    } else {
      setInstallments([...paidInstallments, ...rows]);
      setSplitCount('');
    }
  };

  // Save custom installments
  const handleSaveCustomInstallments = async () => {
    const currentTargetId = getActiveOrderId();
    if (!currentTargetId) {
      Swal.fire({ icon: 'warning', title: 'Save First', text: 'Please save the order before adding installments' });
      return;
    }
    const unpaidRows = installments.filter(
      (inst) => !inst.is_paid && !(parseFloat(inst.paid_amount || 0) > 0) && !isEmptyInstallment(inst)
    );
    if (unpaidRows.length === 0 && installments.length === 0) return;

    try {
      const response = await postRequest(
        POST_URL.orders_v2.api + currentTargetId + '/installments/',
        { installments: unpaidRows }
      );
      if (response && response.status === 200) {
        bindServerOrder(response.data);
        Swal.fire({ icon: 'success', title: 'Installments Saved', timer: 1500, showConfirmButton: false });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save installments' });
    }
  };

  // When amount on an installment exceeds its pending, fill it and push the
  // remainder onto subsequent unpaid installments. Call on blur/submit only —
  // never while typing, or toFixed() breaks decimal input.
  const applyInstallmentAmountWithOverflow = (paymentsMap, startItem, maxPayable) => {
    const updated = { ...paymentsMap };
    const rawVal = updated[startItem.key]?.amount;
    const num = parseFloat(rawVal);

    if (rawVal === '' || rawVal == null || isNaN(num) || num <= 0) {
      updated[startItem.key] = { selected: false, amount: rawVal || '' };
      return updated;
    }

    const startIdx = payableInstallmentItems.findIndex((it) => it.key === startItem.key);
    const pending = startItem.pendingAmount > 0 ? startItem.pendingAmount : startItem.totalAmount;
    const isInstallmentRow = String(startItem.key || '').startsWith('inst-');

    let remaining = num;
    if (maxPayable > 0) {
      let otherSelected = 0;
      payableInstallmentItems.forEach((it, idx) => {
        if (idx === startIdx) return;
        if (idx > startIdx && isInstallmentRow && String(it.key || '').startsWith('inst-')) return;
        if (updated[it.key]?.selected) {
          otherSelected += parseFloat(updated[it.key]?.amount) || 0;
        }
      });
      remaining = Math.min(remaining, Math.max(0, maxPayable - otherSelected));
    }

    if (!isInstallmentRow || startIdx < 0 || remaining <= pending + 0.009) {
      const capped = pending > 0 ? Math.min(remaining, pending) : remaining;
      updated[startItem.key] = {
        selected: capped > 0,
        amount: capped > 0 ? capped.toFixed(2) : '',
      };
      return updated;
    }

    for (let i = startIdx; i < payableInstallmentItems.length; i++) {
      const it = payableInstallmentItems[i];
      if (String(it.key || '').startsWith('inst-')) {
        updated[it.key] = { selected: false, amount: '' };
      }
    }

    for (let i = startIdx; i < payableInstallmentItems.length && remaining > 0.009; i++) {
      const it = payableInstallmentItems[i];
      if (!String(it.key || '').startsWith('inst-') || it.isPaid || it.pendingAmount <= 0) continue;
      const apply = Math.min(remaining, it.pendingAmount);
      updated[it.key] = {
        selected: apply > 0,
        amount: apply.toFixed(2),
      };
      remaining -= apply;
    }

    return updated;
  };

  const sumSelectedPayments = (paymentsMap) => {
    let sum = 0;
    let anySelected = false;
    Object.keys(paymentsMap).forEach((k) => {
      if (paymentsMap[k]?.selected) {
        anySelected = true;
        sum += parseFloat(paymentsMap[k]?.amount) || 0;
      }
    });
    return { sum, anySelected };
  };

  const normalizeOverflowAllocations = (paymentsMap, maxPayable) => {
    let updated = { ...paymentsMap };
    payableInstallmentItems.forEach((it) => {
      if (!String(it.key || '').startsWith('inst-')) return;
      if (!updated[it.key]?.selected) return;
      const amt = parseFloat(updated[it.key]?.amount) || 0;
      if (amt > (it.pendingAmount || 0) + 0.009) {
        updated = applyInstallmentAmountWithOverflow(updated, it, maxPayable);
      }
    });
    return updated;
  };

  // Side panel handlers for installment items
  const handleToggleSidePayableItem = (item, isChecked) => {
    if (item.isPaid || item.pendingAmount <= 0) return;
    const updated = { ...sideItemPayments };
    if (isChecked) {
      const defaultAmt = item.pendingAmount > 0 ? item.pendingAmount.toFixed(2) : (item.totalAmount > 0 ? item.totalAmount.toFixed(2) : '');
      const prevAmt = updated[item.key]?.amount;
      const validAmt = prevAmt && parseFloat(prevAmt) > 0 && parseFloat(prevAmt) <= (item.pendingAmount || item.totalAmount)
        ? prevAmt
        : defaultAmt;
      updated[item.key] = {
        selected: true,
        amount: validAmt,
      };
    } else {
      updated[item.key] = {
        selected: false,
        amount: '',
      };
    }
    setSideItemPayments(updated);
    let sum = 0;
    let anySelected = false;
    Object.keys(updated).forEach((k) => {
      if (updated[k]?.selected) {
        anySelected = true;
        sum += (parseFloat(updated[k]?.amount) || 0);
      }
    });
    if (anySelected) {
      const maxPayable = balanceAmount > 0 ? balanceAmount : computedTotal;
      const finalAmt = maxPayable > 0 && sum > maxPayable ? maxPayable : sum;
      setPaymentForm((prev) => ({ ...prev, amount: finalAmt > 0 ? finalAmt.toFixed(2) : '' }));
    }
  };

  const handleSideItemAmountChange = (item, val) => {
    if (item.isPaid || item.pendingAmount <= 0) return;
    const maxPayable = balanceAmount > 0 ? balanceAmount : computedTotal;
    let num = parseFloat(val);
    // Keep raw string while typing; only hard-cap against order balance
    if (val !== '' && !isNaN(num) && maxPayable > 0 && num > maxPayable) {
      val = maxPayable.toFixed(2);
      num = maxPayable;
    }
    const updated = {
      ...sideItemPayments,
      [item.key]: {
        selected: val !== '' && !isNaN(num) && num > 0,
        amount: val,
      },
    };
    setSideItemPayments(updated);
    const { sum, anySelected } = sumSelectedPayments(updated);
    if (anySelected) {
      const finalAmt = maxPayable > 0 && sum > maxPayable ? maxPayable : sum;
      setPaymentForm((prev) => ({ ...prev, amount: finalAmt > 0 ? finalAmt.toFixed(2) : '' }));
    }
  };

  const handleSideItemAmountBlur = (item) => {
    if (item.isPaid || item.pendingAmount <= 0) return;
    const maxPayable = balanceAmount > 0 ? balanceAmount : computedTotal;
    const updated = applyInstallmentAmountWithOverflow(sideItemPayments, item, maxPayable);
    setSideItemPayments(updated);
    const { sum, anySelected } = sumSelectedPayments(updated);
    if (anySelected) {
      const finalAmt = maxPayable > 0 && sum > maxPayable ? maxPayable : sum;
      setPaymentForm((prev) => ({ ...prev, amount: finalAmt > 0 ? finalAmt.toFixed(2) : '' }));
    }
  };

  const handleToggleAllSidePayableItems = () => {
    if (selectablePayableItems.length === 0) return;
    const isAllSelected = selectablePayableItems.every((it) => sideItemPayments[it.key]?.selected);
    const updated = { ...sideItemPayments };
    if (!isAllSelected) {
      selectablePayableItems.forEach((it) => {
        const defaultAmt = it.pendingAmount > 0 ? it.pendingAmount.toFixed(2) : (it.totalAmount > 0 ? it.totalAmount.toFixed(2) : '');
        updated[it.key] = {
          selected: true,
          amount: defaultAmt,
        };
      });
    } else {
      selectablePayableItems.forEach((it) => {
        updated[it.key] = {
          selected: false,
          amount: '',
        };
      });
    }
    setSideItemPayments(updated);
    let sum = 0;
    let anySelected = false;
    Object.keys(updated).forEach((k) => {
      if (updated[k]?.selected) {
        anySelected = true;
        sum += (parseFloat(updated[k]?.amount) || 0);
      }
    });
    const maxPayable = balanceAmount > 0 ? balanceAmount : computedTotal;
    const finalAmt = maxPayable > 0 && sum > maxPayable ? maxPayable : sum;
    setPaymentForm((prev) => ({ ...prev, amount: anySelected && finalAmt > 0 ? finalAmt.toFixed(2) : '' }));
  };

  // Add payment
  const handleAddPayment = async () => {
    if (!getActiveOrderId()) {
      Swal.fire({ icon: 'warning', title: 'Save First', text: 'Please save the order before adding payments' });
      return;
    }

    const maxPayable = balanceAmount > 0 ? balanceAmount : computedTotal;
    const normalizedPayments = normalizeOverflowAllocations(sideItemPayments, maxPayable);
    if (normalizedPayments !== sideItemPayments) {
      setSideItemPayments(normalizedPayments);
      const { sum, anySelected } = sumSelectedPayments(normalizedPayments);
      if (anySelected) {
        const finalAmt = maxPayable > 0 && sum > maxPayable ? maxPayable : sum;
        setPaymentForm((prev) => ({ ...prev, amount: finalAmt > 0 ? finalAmt.toFixed(2) : prev.amount }));
      }
    }

    const amount = parseFloat(
      (() => {
        const { sum, anySelected } = sumSelectedPayments(normalizedPayments);
        if (anySelected) {
          const finalAmt = maxPayable > 0 && sum > maxPayable ? maxPayable : sum;
          return finalAmt > 0 ? finalAmt : paymentForm.amount;
        }
        return paymentForm.amount;
      })()
    );
    if (!amount || amount <= 0) {
      Swal.fire({ icon: 'warning', title: 'Invalid', text: 'Enter a valid payment amount' });
      return;
    }

    if (maxPayable > 0 && amount > (maxPayable + 0.009)) {
      Swal.fire({
        icon: 'warning',
        title: 'Amount Exceeds Balance',
        text: `Payment amount (₹${amount.toFixed(2)}) cannot exceed the outstanding balance of ₹${maxPayable.toFixed(2)}.`,
      });
      return;
    }

    const paidItemsAttempted = payableInstallmentItems.filter(
      (it) => normalizedPayments[it.key]?.selected && (it.isPaid || it.pendingAmount <= 0)
    );
    if (paidItemsAttempted.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Already Received',
        text: `Cannot collect payment for already received installment: ${paidItemsAttempted.map((p) => p.label).join(', ')}`,
      });
      return;
    }

    const selectedItemBreakdown = payableInstallmentItems
      .filter((it) => normalizedPayments[it.key]?.selected && parseFloat(normalizedPayments[it.key]?.amount) > 0)
      .map((it) => `${it.label}: ₹${parseFloat(normalizedPayments[it.key].amount).toFixed(2)}`);
    let finalNotes = paymentForm.notes || '';
    if (selectedItemBreakdown.length > 0 && !finalNotes) {
      finalNotes = selectedItemBreakdown.join(', ');
    }

    setPaymentSubmitting(true);
    try {
      const response = await postRequest(
        POST_URL.orders_v2.api + getActiveOrderId() + '/payments/',
        {
          amount: amount.toFixed(2),
          payment_date: paymentForm.payment_date || undefined,
          method: paymentForm.method,
          cheque_status: paymentForm.method === 'cheque' ? (paymentForm.cheque_status || 'received') : undefined,
          reference: paymentForm.reference,
          notes: finalNotes,
          allocations: payableInstallmentItems
            .filter((it) => normalizedPayments[it.key]?.selected && parsePrice(normalizedPayments[it.key]?.amount) > 0)
            .map((it) => ({
              installment_id: it.id || null,
              item_index: it.id ? null : (it.itemIndex ?? null),
              amount: parsePrice(normalizedPayments[it.key].amount).toFixed(2),
              label: it.label,
            })),
        }
      );
      if (response && response.status === 201) {
        bindServerOrder(response.data);
        setSideItemPayments({});
        setPaymentForm({ amount: '', payment_date: '', method: 'bank_transfer', cheque_status: 'received', reference: '', notes: '' });
        Swal.fire({ icon: 'success', title: 'Payment Added', timer: 1500, showConfirmButton: false });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to add payment' });
    }
    setPaymentSubmitting(false);
  };

  // Delete payment
  const handleDeletePayment = async (pay) => {
    if (!getActiveOrderId() || !pay?.id) return;
    const formattedAmount = formatAmount(pay.amount);
    const dateStr = pay.payment_date ? new Date(pay.payment_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

    const result = await Swal.fire({
      title: 'Delete Collected Amount?',
      text: `Are you sure you want to delete this payment of ${formattedAmount}${dateStr ? ` collected on ${dateStr}` : ''}? Total received amount and installment allocations will be recalculated.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete payment',
    });

    if (result.isConfirmed) {
      setDeletingPaymentId(pay.id);
      try {
        const response = await axiosAPI.post(
          POST_URL.orders_v2.api + getActiveOrderId() + '/delete_payment/',
          { payment_id: pay.id }
        );
        if (response.data) {
          bindServerOrder(response.data);
          Swal.fire({
            icon: 'success',
            title: 'Payment Deleted',
            text: `Payment of ${formattedAmount} has been deleted.`,
            timer: 1600,
            showConfirmButton: false,
          });
        }
      } catch (err) {
        console.error('Delete payment error:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.response?.data?.error || 'Failed to delete payment',
        });
      } finally {
        setDeletingPaymentId(null);
      }
    }
  };

  const handleUpdateChequeStatus = async (pay, newStatus) => {
    if (!getActiveOrderId() || !pay?.id || pay.method !== 'cheque') return;
    if ((pay.cheque_status || 'received') === newStatus) return;
    setUpdatingChequeStatusId(pay.id);
    try {
      const response = await postRequest(
        POST_URL.orders_v2.api + getActiveOrderId() + '/update_payment/',
        { payment_id: pay.id, cheque_status: newStatus }
      );
      if (response && response.status === 200 && response.data) {
        bindServerOrder(response.data);
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: response?.data?.error || 'Failed to update cheque status' });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update cheque status' });
    }
    setUpdatingChequeStatusId(null);
  };

  // Issue invoice
  const handleIssueInvoice = async () => {
    if (!getActiveOrderId()) {
      Swal.fire({ icon: 'warning', title: 'Save First', text: 'Please save the order first' });
      return;
    }

    const result = await Swal.fire({
      title: 'Issue Invoice?',
      text: 'This will generate an invoice number. Continue?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Issue Invoice',
      confirmButtonColor: '#2563eb',
    });

    if (!result.isConfirmed) return;

    try {
      const response = await postRequest(
        POST_URL.orders_v2.api + getActiveOrderId() + '/issue_invoice/',
        {}
      );
      if (response && response.status === 200) {
        bindServerOrder(response.data);
        Swal.fire({ icon: 'success', title: 'Invoice Issued', text: `Invoice # ${response.data.invoice_number}`, timer: 2000, showConfirmButton: false });
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: response?.data?.error || 'Failed to issue invoice' });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to issue invoice' });
    }
  };

  // Print invoice PDF handler
  const selectedAcademicYearIds = () => {
    const years = (order.academic_years && order.academic_years.length > 0)
      ? order.academic_years
      : (order.academic_year ? [order.academic_year] : []);
    return [...new Set(years.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0))];
  };

  const academicYearsQuery = (extra = {}) => {
    const params = new URLSearchParams();
    Object.entries(extra).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.set(key, value);
    });
    const years = selectedAcademicYearIds();
    if (years.length) params.set('academic_years', years.join(','));
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  };

  const handlePrintInvoice = async (kind = 'proforma') => {
    const isPending = kind === 'pending';
    const setBusy = isPending ? setPrintingPending : setPrintingInvoice;
    setBusy(true);
    let targetOrder = orderData;
    const currentTargetId = orderId || targetOrder?.id;

    // Automatically save or update the order first to ensure all service items and changes are persisted
    try {
      const saved = await handleSave(true);
      if (saved && saved.id) {
        targetOrder = saved;
      }
    } catch (saveErr) {
      console.error('Save before print error:', saveErr);
    }

    const orderIdToPrint = targetOrder?.id || currentTargetId;
    if (!orderIdToPrint) {
      setBusy(false);
      Swal.fire({ icon: 'warning', title: 'Save Failed', text: 'Please fill in required fields and save the order first.' });
      return;
    }

    const docLabel = isPending ? 'Pending amount invoice' : 'Proforma';
    try {
      const token = localStorage.getItem('token');
      const response = await axiosAPI.get(
        GET_URL.orders_v2.api + orderIdToPrint + (isPending ? '/pending_invoice_pdf/' : '/invoice_pdf/') + academicYearsQuery(),
        {
          responseType: 'blob',
          headers: { Authorization: `Token ${token}` },
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = window.URL.createObjectURL(blob);
      const printWindow = window.open(fileURL, '_blank');
      if (printWindow) {
        printWindow.focus();
      }

      Swal.fire({
        icon: 'success',
        title: `${docLabel} Opened for Printing`,
        text: `The ${docLabel.toLowerCase()} PDF has been opened in a new tab for printing.`,
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error(`Print ${docLabel} error:`, err);
      Swal.fire({ icon: 'error', title: 'Error', text: `Failed to generate ${docLabel.toLowerCase()} for printing` });
    } finally {
      setBusy(false);
    }
  };

  // Print single installment PDF (same Service/Item layout as top Print Proforma)
  const handlePrintInstallment = async (inst) => {
    const currentTargetId = getActiveOrderId();
    if (!currentTargetId || !inst?.id) {
      Swal.fire({ icon: 'warning', title: 'Save First', text: 'Please save the order before printing installment documents.' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axiosAPI.get(
        GET_URL.orders_v2.api + `${currentTargetId}/installment_pdf/${academicYearsQuery({ installment_id: inst.id })}`,
        {
          responseType: 'blob',
          headers: { Authorization: `Token ${token}` },
        }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = window.URL.createObjectURL(blob);
      const printWindow = window.open(fileURL, '_blank');
      if (printWindow) printWindow.focus();

      const link = document.createElement('a');
      link.href = fileURL;
      link.setAttribute('download', `Proforma_Invoice_Installment_${inst.installment_number || inst.id}_${orderData?.order_number || currentTargetId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Print installment error:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to print installment document' });
    }
  };

  // Print single payment receipt PDF
  const handlePrintPayment = async (pay) => {
    const currentTargetId = getActiveOrderId();
    if (!currentTargetId || !pay?.id) {
      Swal.fire({ icon: 'warning', title: 'Save First', text: 'Please save the order before printing payment receipts.' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axiosAPI.get(
        GET_URL.orders_v2.api + `${currentTargetId}/payment_receipt_pdf/?payment_id=${pay.id}`,
        {
          responseType: 'blob',
          headers: { Authorization: `Token ${token}` },
        }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = window.URL.createObjectURL(blob);
      const printWindow = window.open(fileURL, '_blank');
      if (printWindow) printWindow.focus();

      const link = document.createElement('a');
      link.href = fileURL;
      link.setAttribute('download', `Payment_Receipt_${pay.id}_${orderData?.order_number || currentTargetId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Print payment receipt error:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to print payment receipt' });
    }
  };

  // Print all payment receipts / payment history summary PDF
  const handlePrintAllPayments = async () => {
    const currentTargetId = getActiveOrderId();
    if (!currentTargetId) {
      Swal.fire({ icon: 'warning', title: 'Save First', text: 'Please save the order before printing payment history.' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axiosAPI.get(
        GET_URL.orders_v2.api + `${currentTargetId}/all_payments_receipt_pdf/`,
        {
          responseType: 'blob',
          headers: { Authorization: `Token ${token}` },
        }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = window.URL.createObjectURL(blob);
      const printWindow = window.open(fileURL, '_blank');
      if (printWindow) printWindow.focus();

      const link = document.createElement('a');
      link.href = fileURL;
      link.setAttribute('download', `Payment_Receipts_${orderData?.order_number || currentTargetId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Print payment history error:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to print payment history' });
    }
  };

  const installmentRowKey = (inst, idx) => (inst?.id != null ? `inst-${inst.id}` : `inst-new-${idx}`);
  const excludedRowKey = (exItem, eIdx) => `ex-${exItem.originalIndex ?? eIdx}`;

  const installmentSelectableKeys = React.useMemo(() => {
    const keys = [];
    (installments || []).forEach((inst, idx) => {
      if (inst?.id && !isEmptyInstallment(inst)) keys.push(installmentRowKey(inst, idx));
    });
    (excludedItemsList || []).forEach((exItem, eIdx) => {
      keys.push(excludedRowKey(exItem, eIdx));
    });
    return keys;
  }, [installments, excludedItemsList, isEmptyInstallment]);

  const allInstallmentRowsSelected =
    installmentSelectableKeys.length > 0 &&
    installmentSelectableKeys.every((k) => selectedInstallmentKeys.includes(k));

  React.useEffect(() => {
    setSelectedInstallmentKeys((prev) => prev.filter((k) => installmentSelectableKeys.includes(k)));
  }, [installmentSelectableKeys]);

  const toggleInstallmentRow = (key) => {
    setSelectedInstallmentKeys((prev) => (
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));
  };

  const toggleAllInstallmentRows = () => {
    setSelectedInstallmentKeys((prev) => (
      installmentSelectableKeys.length > 0 && installmentSelectableKeys.every((k) => prev.includes(k))
        ? []
        : [...installmentSelectableKeys]
    ));
  };

  // Print installments table proforma — same Service/Item layout as top Print Proforma.
  // If rows are checked, only those installments / non-split items are included.
  const handlePrintAllInstallments = async () => {
    const currentTargetId = getActiveOrderId();
    if (!currentTargetId) {
      Swal.fire({ icon: 'warning', title: 'Save First', text: 'Please save the order before printing the installment schedule.' });
      return;
    }

    const selectedInstIds = selectedInstallmentKeys
      .filter((k) => k.startsWith('inst-'))
      .map((k) => k.replace('inst-', ''))
      .filter((id) => id && !id.startsWith('new-'));
    const selectedExcludedIdx = selectedInstallmentKeys
      .filter((k) => k.startsWith('ex-'))
      .map((k) => k.replace('ex-', ''))
      .filter((v) => v !== '');

    const extra = {};
    if (selectedInstallmentKeys.length > 0) {
      extra.installment_ids = selectedInstIds.join(',');
      extra.excluded_indices = selectedExcludedIdx.join(',');
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axiosAPI.get(
        GET_URL.orders_v2.api + `${currentTargetId}/installments_schedule_pdf/${academicYearsQuery(extra)}`,
        {
          responseType: 'blob',
          headers: { Authorization: `Token ${token}` },
        }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = window.URL.createObjectURL(blob);
      const printWindow = window.open(fileURL, '_blank');
      if (printWindow) printWindow.focus();

      const link = document.createElement('a');
      link.href = fileURL;
      link.setAttribute('download', `Proforma_Invoice_Installments_Schedule_${orderData?.order_number || currentTargetId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Print installment schedule error:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to print installment schedule' });
    }
  };

  // Print single individual service item demand note
  const handlePrintItem = async (item, itemIndex = null, fromInstallment = false) => {
    const currentTargetId = getActiveOrderId();
    if (!currentTargetId) {
      Swal.fire({ icon: 'warning', title: 'Save First', text: 'Please save the order before printing item documents.' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axiosAPI.post(
        GET_URL.orders_v2.api + `${currentTargetId}/item_pdf/${academicYearsQuery()}`,
        {
          item_index: itemIndex,
          item_name: item?.name,
          item_data: item,
          from_installment: Boolean(fromInstallment),
        },
        {
          responseType: 'blob',
          headers: { Authorization: `Token ${token}` },
        }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = window.URL.createObjectURL(blob);
      const printWindow = window.open(fileURL, '_blank');
      if (printWindow) printWindow.focus();

      const safeName = (item?.name || 'Item').replace(/[^a-zA-Z0-9_-]/g, '_');
      const link = document.createElement('a');
      link.href = fileURL;
      link.setAttribute('download', `Proforma_Invoice_${safeName}_${orderData?.order_number || currentTargetId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Print item error:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to print item document' });
    }
  };

  // Open dedicated Add Amount modal
  const handleOpenAddAmountModal = async () => {
    const currentTargetId = getActiveOrderId();
    if (!currentTargetId) {
      const confirmSave = await Swal.fire({
        title: 'Save Order First?',
        text: 'Please save the order before adding payments or amounts. Save now?',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Save & Add Amount',
        confirmButtonColor: '#059669',
      });
      if (!confirmSave.isConfirmed) return;
      const saved = await handleSave(true);
      if (!saved?.id) return;
    }

    // Prefill with outstanding balance if positive
    const remaining = balanceAmount > 0 ? balanceAmount.toFixed(2) : '';
    setItemPayments({});
    setModalPayment({
      amount: remaining,
      payment_date: new Date().toISOString().slice(0, 16),
      method: 'bank_transfer',
      cheque_status: 'received',
      reference: '',
      notes: '',
    });
    setAddAmountModalOpen(true);
  };

  const handleTogglePayableItem = (item, isChecked) => {
    if (item.isPaid || item.pendingAmount <= 0) return;
    const updated = { ...itemPayments };
    if (isChecked) {
      const defaultAmt = item.pendingAmount > 0 ? item.pendingAmount.toFixed(2) : (item.totalAmount > 0 ? item.totalAmount.toFixed(2) : '');
      const prevAmt = updated[item.key]?.amount;
      const validAmt = prevAmt && parseFloat(prevAmt) > 0 && parseFloat(prevAmt) <= (item.pendingAmount || item.totalAmount)
        ? prevAmt
        : defaultAmt;
      updated[item.key] = {
        selected: true,
        amount: validAmt,
      };
    } else {
      updated[item.key] = {
        selected: false,
        amount: '',
      };
    }
    setItemPayments(updated);
    let sum = 0;
    let anySelected = false;
    Object.keys(updated).forEach((k) => {
      if (updated[k]?.selected) {
        anySelected = true;
        sum += (parseFloat(updated[k]?.amount) || 0);
      }
    });
    if (anySelected) {
      const maxPayable = balanceAmount > 0 ? balanceAmount : computedTotal;
      const finalAmt = maxPayable > 0 && sum > maxPayable ? maxPayable : sum;
      setModalPayment((prev) => ({ ...prev, amount: finalAmt > 0 ? finalAmt.toFixed(2) : '' }));
    }
  };

  const handleItemAmountChange = (item, val) => {
    if (item.isPaid || item.pendingAmount <= 0) return;
    const maxPayable = balanceAmount > 0 ? balanceAmount : computedTotal;
    let num = parseFloat(val);
    // Keep raw string while typing; only hard-cap against order balance
    if (val !== '' && !isNaN(num) && maxPayable > 0 && num > maxPayable) {
      val = maxPayable.toFixed(2);
      num = maxPayable;
    }
    const updated = {
      ...itemPayments,
      [item.key]: {
        selected: val !== '' && !isNaN(num) && num > 0,
        amount: val,
      },
    };
    setItemPayments(updated);
    const { sum, anySelected } = sumSelectedPayments(updated);
    if (anySelected) {
      const finalAmt = maxPayable > 0 && sum > maxPayable ? maxPayable : sum;
      setModalPayment((prev) => ({ ...prev, amount: finalAmt > 0 ? finalAmt.toFixed(2) : '' }));
    }
  };

  const handleItemAmountBlur = (item) => {
    if (item.isPaid || item.pendingAmount <= 0) return;
    const maxPayable = balanceAmount > 0 ? balanceAmount : computedTotal;
    const updated = applyInstallmentAmountWithOverflow(itemPayments, item, maxPayable);
    setItemPayments(updated);
    const { sum, anySelected } = sumSelectedPayments(updated);
    if (anySelected) {
      const finalAmt = maxPayable > 0 && sum > maxPayable ? maxPayable : sum;
      setModalPayment((prev) => ({ ...prev, amount: finalAmt > 0 ? finalAmt.toFixed(2) : '' }));
    }
  };

  const handleToggleAllPayableItems = () => {
    if (selectablePayableItems.length === 0) return;
    const isAllSelected = selectablePayableItems.every((it) => itemPayments[it.key]?.selected);
    const updated = { ...itemPayments };
    if (!isAllSelected) {
      selectablePayableItems.forEach((it) => {
        const defaultAmt = it.pendingAmount > 0 ? it.pendingAmount.toFixed(2) : (it.totalAmount > 0 ? it.totalAmount.toFixed(2) : '');
        updated[it.key] = {
          selected: true,
          amount: defaultAmt,
        };
      });
    } else {
      selectablePayableItems.forEach((it) => {
        updated[it.key] = {
          selected: false,
          amount: '',
        };
      });
    }
    setItemPayments(updated);
    let sum = 0;
    let anySelected = false;
    Object.keys(updated).forEach((k) => {
      if (updated[k]?.selected) {
        anySelected = true;
        sum += (parseFloat(updated[k]?.amount) || 0);
      }
    });
    const maxPayable = balanceAmount > 0 ? balanceAmount : computedTotal;
    const finalAmt = maxPayable > 0 && sum > maxPayable ? maxPayable : sum;
    setModalPayment((prev) => ({ ...prev, amount: anySelected && finalAmt > 0 ? finalAmt.toFixed(2) : '' }));
  };

  // Submit payment from Add Amount modal
  const handleModalPaymentSubmit = async () => {
    const maxPayable = balanceAmount > 0 ? balanceAmount : computedTotal;
    const normalizedPayments = normalizeOverflowAllocations(itemPayments, maxPayable);
    setItemPayments(normalizedPayments);

    const { sum, anySelected } = sumSelectedPayments(normalizedPayments);
    let amt = parseFloat(modalPayment.amount);
    if (anySelected) {
      const finalAmt = maxPayable > 0 && sum > maxPayable ? maxPayable : sum;
      amt = finalAmt > 0 ? finalAmt : amt;
      setModalPayment((prev) => ({ ...prev, amount: amt > 0 ? amt.toFixed(2) : prev.amount }));
    }

    if (!amt || isNaN(amt) || amt <= 0) {
      Swal.fire({ icon: 'warning', title: 'Invalid Amount', text: 'Please enter a valid payment amount greater than 0' });
      return;
    }

    if (maxPayable > 0 && amt > (maxPayable + 0.009)) {
      Swal.fire({
        icon: 'warning',
        title: 'Amount Exceeds Balance',
        text: `Payment amount (₹${amt.toFixed(2)}) cannot exceed the outstanding balance of ₹${maxPayable.toFixed(2)}.`,
      });
      return;
    }

    const paidItemsAttempted = payableInstallmentItems.filter(
      (it) => normalizedPayments[it.key]?.selected && (it.isPaid || it.pendingAmount <= 0)
    );
    if (paidItemsAttempted.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Already Received',
        text: `Cannot collect payment for already received installment: ${paidItemsAttempted.map((p) => p.label).join(', ')}`,
      });
      return;
    }

    const currentTargetId = getActiveOrderId();
    if (!currentTargetId) return;

    const selectedItemBreakdown = payableInstallmentItems
      .filter((it) => normalizedPayments[it.key]?.selected && parseFloat(normalizedPayments[it.key]?.amount) > 0)
      .map((it) => `${it.label}: ₹${parseFloat(normalizedPayments[it.key].amount).toFixed(2)}`);
    let finalNotes = modalPayment.notes || '';
    if (selectedItemBreakdown.length > 0 && !finalNotes) {
      finalNotes = selectedItemBreakdown.join(', ');
    }

    setModalPaymentSubmitting(true);
    try {
      const response = await postRequest(
        POST_URL.orders_v2.api + currentTargetId + '/payments/',
        {
          amount: amt,
          payment_date: modalPayment.payment_date ? modalPayment.payment_date.replace('T', ' ') : undefined,
          method: modalPayment.method,
          cheque_status: modalPayment.method === 'cheque' ? (modalPayment.cheque_status || 'received') : undefined,
          reference: modalPayment.reference,
          notes: finalNotes,
          allocations: payableInstallmentItems
            .filter((it) => normalizedPayments[it.key]?.selected && parsePrice(normalizedPayments[it.key]?.amount) > 0)
            .map((it) => ({
              installment_id: it.id || null,
              item_index: it.id ? null : (it.itemIndex ?? null),
              amount: parsePrice(normalizedPayments[it.key].amount).toFixed(2),
              label: it.label,
            })),
        }
      );

      if (response && response.status === 201) {
        bindServerOrder(response.data);
        setAddAmountModalOpen(false);
        Swal.fire({
          icon: 'success',
          title: 'Amount Added Successfully',
          text: `Payment of ₹${amt.toLocaleString('en-IN', { minimumFractionDigits: 2 })} recorded.`,
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: response?.data?.error || 'Failed to add amount' });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to record amount' });
    } finally {
      setModalPaymentSubmitting(false);
    }
  };

  // Download invoice PDF
  const handleDownloadInvoice = handlePrintInvoice;

  const handleDeleteOrder = async () => {
    const targetId = getActiveOrderId();
    if (!targetId) return;

    const result = await Swal.fire({
      title: 'Delete Order?',
      text: `Are you sure you want to delete ${orderData?.order_number || 'this order'}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete order',
    });

    if (result.isConfirmed) {
      try {
        await axiosAPI.delete(GET_URL.orders_v2.api + targetId + '/');
        Swal.fire({
          icon: 'success',
          title: 'Order Deleted',
          text: `${orderData?.order_number || 'Order'} has been deleted.`,
          timer: 1800,
          showConfirmButton: false,
        });
        navigate('/billing/orders-v2/list');
      } catch (err) {
        console.error('Delete order error:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err?.response?.data?.error || 'Failed to delete order',
        });
      }
    }
  };

  // Add new installment row
  const addNewInstallmentRow = () => {
    const nextNum = installments.length + 1;
    setInstallments([
      ...installments,
      {
        installment_number: nextNum,
        label: `Installment ${nextNum}`,
        percentage: '',
        amount: '',
        due_date: '',
        paid_amount: '0.00',
        is_paid: false,
      },
    ]);
  };

  const updateInstallment = (index, field, value) => {
    const updated = [...installments];
    const row = { ...updated[index], [field]: value };

    if (field === 'percentage') {
      const pct = parseFloat(value);
      if (!isNaN(pct) && targetSplitPool > 0) {
        row.amount = ((targetSplitPool * pct) / 100).toFixed(2);
      } else if (value === '') {
        row.amount = '';
      }
    } else if (field === 'amount') {
      const amt = parseFloat(value);
      if (!isNaN(amt) && targetSplitPool > 0) {
        row.percentage = ((amt / targetSplitPool) * 100).toFixed(2);
      } else if (value === '') {
        row.percentage = '';
      }
    }

    updated[index] = row;
    setInstallments(updated);
  };

  const removeInstallment = (index) => {
    const item = installments[index];
    if (item && (item.is_paid || parseFloat(item.paid_amount || 0) > 0)) {
      Swal.fire({ icon: 'warning', title: 'Cannot Delete', text: 'Paid installments cannot be deleted.' });
      return;
    }
    setInstallments(installments.filter((_, i) => i !== index));
  };

  const visibleInstallmentCount = installments.filter((inst) => !isEmptyInstallment(inst)).length;
  const unpaidInstallments = installments.filter((inst) => !inst.is_paid && !(parseFloat(inst.paid_amount || 0) > 0));
  const totalInstallmentPercentage = receivedAmount > 0
    ? unpaidInstallments.reduce((sum, inst) => sum + (parseFloat(inst.percentage) || 0), 0)
    : installments.reduce((sum, inst) => sum + (parseFloat(inst.percentage) || 0), 0);
  const totalInstallmentAmount = installments.reduce((sum, inst) => sum + (parseFloat(inst.amount) || 0), 0);
  const installmentPctMatched = Math.abs(totalInstallmentPercentage - 100) < 0.01;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, width: '100%', boxSizing: 'border-box' }}>
      {/* Top Action Bar */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2.5,
        flexWrap: 'wrap',
        gap: 1.5,
      }}>
        {/* Status Slider Switch */}
        <Box sx={{
          display: 'flex',
          bgcolor: '#f1f5f9',
          borderRadius: 2.5,
          p: '3px',
          gap: '3px',
          overflowX: 'auto',
          alignItems: 'center',
        }}>
          {[
            { value: 'draft', label: 'Draft', activeBg: STATUS_COLORS.draft.bg, activeColor: STATUS_COLORS.draft.color },
            { value: 'invoice_issued', label: 'Issued', activeBg: STATUS_COLORS.invoice_issued.bg, activeColor: STATUS_COLORS.invoice_issued.color },
            { value: 'partially_collected', label: 'Partially Paid', activeBg: STATUS_COLORS.partially_collected.bg, activeColor: STATUS_COLORS.partially_collected.color },
            { value: 'amount_collected', label: 'Paid', activeBg: STATUS_COLORS.amount_collected.bg, activeColor: STATUS_COLORS.amount_collected.color },
          ].map((btn) => {
            const isActive = currentStatus === btn.value;
            return (
              <Button
                key={btn.value}
                size="small"
                onClick={() => handleStatusChange(btn.value)}
                sx={{
                  minWidth: 0,
                  px: { xs: 1.5, sm: 2 },
                  py: 0.6,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: isActive ? 700 : 500,
                  fontSize: 12,
                  whiteSpace: 'nowrap',
                  background: isActive ? btn.activeBg : 'transparent',
                  color: isActive ? btn.activeColor : '#64748b',
                  boxShadow: isActive ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: isActive ? btn.activeBg : '#e2e8f0',
                  },
                }}
              >
                {isActive && (
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: btn.activeColor, mr: 0.8, flexShrink: 0 }} />
                )}
                {btn.label}
              </Button>
            );
          })}
        </Box>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Assigned to</InputLabel>
          <Select
            label="Assigned to"
            value={assignedTo}
            disabled={updatingAssignee || !getActiveOrderId()}
            onChange={async (e) => {
              const orderIdToAssign = getActiveOrderId();
              if (!orderIdToAssign) return;
              const nextValue = e.target.value;
              const previous = assignedTo;
              setAssignedTo(nextValue);
              setUpdatingAssignee(true);
              try {
                const response = await postRequest(
                  GET_URL.orders_v2.api + orderIdToAssign + '/set_assignee/',
                  { assigned_to: nextValue || null },
                  { return_error: true }
                );
                if (response && (response.status === 200 || response.status === 201)) {
                  const saved = response.data || {};
                  setAssignedTo(saved.assigned_to ? String(saved.assigned_to) : '');
                  setOrderData((prev) => prev ? { ...prev, assigned_to: saved.assigned_to || null, assigned_to_name: saved.assigned_to_name || '' } : prev);
                } else {
                  setAssignedTo(previous);
                  Swal.fire({ icon: 'error', title: 'Could not assign order' });
                }
              } catch (err) {
                setAssignedTo(previous);
                Swal.fire({ icon: 'error', title: 'Could not assign order', text: err?.response?.data?.error || '' });
              } finally {
                setUpdatingAssignee(false);
              }
            }}
          >
            <MenuItem value="">Unassigned</MenuItem>
            {assignedTo && !assigneeUsers.some((user) => String(user.id) === String(assignedTo)) && (
              <MenuItem value={assignedTo}>{orderData?.assigned_to_name || 'Assigned user'}</MenuItem>
            )}
            {assigneeUsers.map((user) => (
              <MenuItem key={user.id} value={String(user.id)}>
                {user.username || user.email || user.id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: { xs: '100%', sm: 'auto' }, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
          {/* Button 1: Print Invoice */}
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => handlePrintInvoice('proforma')}
            disabled={printingInvoice}
            sx={{
              flex: { xs: 1, sm: 'none' },
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              borderColor: '#2563eb',
              color: '#2563eb',
              px: 2,
              py: 0.9,
              whiteSpace: 'nowrap',
              '&:hover': { borderColor: '#1d4ed8', bgcolor: '#eff6ff' },
            }}
          >
            {printingInvoice ? 'Printing...' : 'Print Proforma'}
          </Button>

          <Tooltip title="Invoice with paid and pending amount">
            <span>
              <Button
                variant="outlined"
                startIcon={<ReceiptIcon />}
                onClick={() => handlePrintInvoice('pending')}
                disabled={printingPending}
                sx={{
                  flex: { xs: 1, sm: 'none' },
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  borderColor: '#dc2626',
                  color: '#dc2626',
                  px: 2,
                  py: 0.9,
                  whiteSpace: 'nowrap',
                  '&:hover': { borderColor: '#b91c1c', bgcolor: '#fef2f2' },
                }}
              >
                {printingPending ? 'Printing...' : 'Pending Invoice'}
              </Button>
            </span>
          </Tooltip>

          {/* Button 2: Add Amount */}
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenAddAmountModal}
            sx={{
              flex: { xs: 1, sm: 'none' },
              background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              px: 2.5,
              py: 0.9,
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)',
              '&:hover': { background: 'linear-gradient(135deg, #047857 0%, #059669 100%)' },
            }}
          >
            Add Amount
          </Button>

          {/* Button 3: Save Order */}
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => handleSave()}
            disabled={saving}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              px: 3,
              py: 0.9,
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(37, 99, 235, 0.25)',
              '&:hover': { background: 'linear-gradient(135deg, #1d4ed8 0%, #6d28d9 100%)' },
            }}
          >
            {saving ? 'Saving...' : 'Save Order'}
          </Button>

          {/* Button 4: Delete Order */}
          {Boolean(getActiveOrderId()) && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteOrder}
              sx={{
                flex: { xs: 1, sm: 'none' },
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                borderColor: '#fca5a5',
                color: '#dc2626',
                px: 2,
                py: 0.9,
                whiteSpace: 'nowrap',
                '&:hover': { borderColor: '#dc2626', bgcolor: '#fef2f2' },
              }}
            >
              Delete Order
            </Button>
          )}
        </Stack>
      </Box>

      {/* Summary KPI Cards */}
      {isEdit && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, borderRadius: 2.5, border: '1px solid #dbeafe', bgcolor: '#f8faff' }}>
              <Typography variant="caption" sx={{ color: '#1d4ed8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                Total Amount
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e3a8a', fontSize: { xs: 16, sm: 20 } }}>
                {formatAmount(computedTotal)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, borderRadius: 2.5, border: '1px solid #d1fae5', bgcolor: '#f0fdf4' }}>
              <Typography variant="caption" sx={{ color: '#047857', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                Received <LockIcon sx={{ fontSize: 12 }} />
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#065f46', fontSize: { xs: 16, sm: 20 } }}>
                {formatAmount(receivedAmount)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, borderRadius: 2.5, border: balanceAmount > 0 ? '1px solid #fecaca' : '1px solid #d1fae5', bgcolor: balanceAmount > 0 ? '#fef2f2' : '#f0fdf4' }}>
              <Typography variant="caption" sx={{ color: balanceAmount > 0 ? '#b91c1c' : '#047857', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                Balance Due
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: balanceAmount > 0 ? '#991b1b' : '#065f46', fontSize: { xs: 16, sm: 20 } }}>
                {formatAmount(balanceAmount)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, borderRadius: 2.5, border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                Invoice #
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: invoiceNumber ? '#2563eb' : '#94a3b8', fontSize: { xs: 16, sm: 20 } }}>
                {invoiceNumber || 'Not Issued'}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Grid container spacing={2.5}>
        {/* Left Column - Order Details */}
        <Grid item xs={12} lg={7} xl={7.5}>
          {/* Order Details */}
          <Paper sx={sectionStyle}>
            <Typography sx={sectionTitleStyle}>📋 Order Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                  <Tooltip title="Previous client">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => moveClient(-1)}
                        disabled={!sortedCompanies.length}
                        sx={{
                          mt: 0.4,
                          bgcolor: '#eff6ff',
                          border: '1px solid #bfdbfe',
                          '&:hover': { bgcolor: '#dbeafe' },
                        }}
                      >
                        <ChevronLeftIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Autocomplete
                      size="small"
                      options={sortedCompanies}
                      getOptionLabel={(opt) => {
                        if (!opt) return '';
                        return opt.code ? `${opt.name} (${opt.code})` : (opt.name || '');
                      }}
                      filterOptions={(options, { inputValue }) => {
                        const q = (inputValue || '').toLowerCase().trim();
                        if (!q) return options;
                        return options.filter(
                          (opt) =>
                            (opt.name && opt.name.toLowerCase().includes(q)) ||
                            (opt.code && opt.code.toLowerCase().includes(q))
                        );
                      }}
                      value={sortedCompanies.find((c) => Number(c.id) === Number(order.company)) || null}
                      onChange={(e, val) => handleCompanyChange(val)}
                      renderOption={(props, opt) => (
                        <li {...props} key={opt.id}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', py: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                              {opt.name}
                            </Typography>
                            {opt.code && (
                              <Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 600 }}>
                                School Code: {opt.code}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Client / School"
                          placeholder="Search by school name or code..."
                          helperText={
                            clientStatusFilter && clientStatusFilter !== 'all'
                              ? `${TEAM_STATUS_LABELS[clientStatusFilter] || clientStatusFilter} · ${currentCompanyIndex >= 0 ? currentCompanyIndex + 1 : 0} of ${sortedCompanies.length}`
                              : (sortedCompanies.length ? `${currentCompanyIndex >= 0 ? currentCompanyIndex + 1 : 0} of ${sortedCompanies.length}` : '')
                          }
                        />
                      )}
                      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                    />
                  </Box>
                  <Tooltip title="Next client">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => moveClient(1)}
                        disabled={!sortedCompanies.length}
                        sx={{
                          mt: 0.4,
                          bgcolor: '#eff6ff',
                          border: '1px solid #bfdbfe',
                          '&:hover': { bgcolor: '#dbeafe' },
                        }}
                      >
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="academic-year-multi-label">Academic Year(s)</InputLabel>
                  <Select
                    labelId="academic-year-multi-label"
                    multiple
                    value={(order.academic_years && order.academic_years.length > 0
                      ? order.academic_years
                      : (order.academic_year ? [order.academic_year] : [])
                    ).map((id) => Number(id))}
                    label="Academic Year(s)"
                    onChange={(e) => handleAcademicYearsChange(e.target.value)}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((val) => {
                          const yr = yearList.find((y) => Number(y.id) === Number(val));
                          return (
                            <Chip
                              key={val}
                              label={yr ? yr.name : val}
                              size="small"
                              onMouseDown={(e) => e.stopPropagation()}
                              onDelete={() => {
                                const next = selected.filter((id) => Number(id) !== Number(val));
                                handleAcademicYearsChange(next);
                              }}
                              sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: '#eff6ff', color: '#1d4ed8' }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {yearList.map((yr) => {
                      const selectedIds = (order.academic_years && order.academic_years.length > 0
                        ? order.academic_years
                        : (order.academic_year ? [order.academic_year] : [])).map((id) => Number(id));
                      const isChecked = selectedIds.includes(Number(yr.id));
                      return (
                        <MenuItem key={yr.id} value={Number(yr.id)}>
                          <Checkbox checked={isChecked} size="small" />
                          <ListItemText primary={yr.name} />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  label="Notes"
                  multiline
                  rows={2}
                  value={order.notes}
                  onChange={(e) => setOrder({ ...order, notes: e.target.value })}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Services / Line Items Section */}
          <Paper sx={sectionStyle}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={sectionTitleStyle}>
                  <ShoppingCartIcon sx={{ fontSize: 18, color: '#2563eb' }} /> Services / Opted Features
                </Typography>
                <Chip
                  label={`${serviceItems.length} item${serviceItems.length !== 1 ? 's' : ''}`}
                  size="small"
                  color={serviceItems.length > 0 ? 'primary' : 'default'}
                  sx={{ fontWeight: 600, fontSize: 12 }}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => addServiceItem(true)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 1.5,
                    fontSize: 12,
                    borderColor: '#2563eb',
                    color: '#2563eb',
                    '&:hover': { background: '#eff6ff', borderColor: '#1d4ed8' },
                  }}
                >
                  Add Fixed Amount
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => addServiceItem(false)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 1.5,
                    fontSize: 12,
                    background: '#2563eb',
                    '&:hover': { background: '#1d4ed8' },
                  }}
                >
                  Add Service
                </Button>
              </Box>
            </Box>

            {serviceItems.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: '#94a3b8' }}>
                <ShoppingCartIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
                <Typography variant="body2">No services added yet. Click "Add Service" to get started.</Typography>
              </Box>
            ) : (
              <>
                <TableContainer sx={{ overflowX: 'auto', width: '100%', borderRadius: 2, border: '1px solid #e2e8f0', mb: 1.5 }}>
                  <Table size="small" sx={{ minWidth: 720 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc', '& th': { fontWeight: 700, fontSize: 11, color: '#475569', textTransform: 'uppercase', whiteSpace: 'nowrap', py: 1.2 } }}>
                        <TableCell>Service Name</TableCell>
                        <TableCell align="right">Price (₹)</TableCell>
                        <TableCell align="center" sx={{ width: 140 }}>Amount Type</TableCell>
                        <TableCell align="right" sx={{ width: 90 }}>Count / Tenure</TableCell>
                        <TableCell align="center" sx={{ width: 60 }}>GST</TableCell>
                        <TableCell align="right" sx={{ width: 80 }}>GST %</TableCell>
                        <TableCell align="center" sx={{ width: 110 }}>In Split?</TableCell>
                        <TableCell align="right">Subtotal</TableCell>
                        <TableCell align="center" sx={{ width: 40 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {serviceItems.map((item, idx) => {
                        const price = parsePrice(item.price);
                        const count = parsePrice(item.count) || 1;
                        const isFixed = Boolean(item.is_fixed || item.amount_fixed || item.erpAmountFixed);
                        let lineBase = isFixed ? price : (price * count);
                        let gstAmt = 0;
                        if (item.has_gst) {
                          const gstPct = parseFloat(item.gst_percentage) || 0;
                          gstAmt = lineBase * gstPct / 100;
                        }
                        const lineTotal = lineBase + gstAmt;

                        return (
                          <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                            <TableCell sx={{ minWidth: 180 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {!serviceNameIsKnown(item.name) && (
                                  <Tooltip title={`Add "${item.name}" to Other Services`}>
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={addingOtherServiceIdx !== null}
                                        onClick={() => handleAddOtherService(idx, item.name)}
                                        sx={{ color: '#2563eb', p: 0.25 }}
                                      >
                                        {addingOtherServiceIdx === idx
                                          ? <CircularProgress size={16} />
                                          : <AddIcon fontSize="small" />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                                <Autocomplete
                                  size="small"
                                  freeSolo
                                  options={serviceSuggestions}
                                  filterOptions={(options, params) => {
                                    const filtered = filterServiceOptions(options, params);
                                    const input = String(params.inputValue || '').trim();
                                    const exists = options.some(
                                      (opt) => String(opt?.name || '').trim().toLowerCase() === input.toLowerCase()
                                    );
                                    if (input && !exists) {
                                      filtered.push({ name: input, isNew: true });
                                    }
                                    return filtered;
                                  }}
                                  getOptionLabel={(option) => (
                                    typeof option === 'string' ? option : (option?.name || '')
                                  )}
                                  isOptionEqualToValue={(option, value) => {
                                    const optName = typeof option === 'string' ? option : option?.name;
                                    const valName = typeof value === 'string' ? value : value?.name;
                                    return String(optName || '') === String(valName || '');
                                  }}
                                  renderOption={(props, option) => (
                                    <li {...props} key={option.isNew ? `new-${option.name}` : option.name}>
                                      {option.isNew ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}>
                                          <AddIcon fontSize="small" sx={{ color: '#2563eb' }} />
                                          <span>{option.name}</span>
                                          <Typography variant="caption" sx={{ ml: 'auto', color: '#64748b' }}>
                                            Other Service
                                          </Typography>
                                        </Box>
                                      ) : option.name}
                                    </li>
                                  )}
                                  value={item.name || ''}
                                  onInputChange={(e, val, reason) => {
                                    if (reason === 'input' || reason === 'clear') {
                                      updateServiceItem(idx, 'name', val);
                                    }
                                  }}
                                  onChange={(e, val) => {
                                    if (val && typeof val === 'object' && val.isNew) {
                                      handleAddOtherService(idx, val.name);
                                      return;
                                    }
                                    const name = typeof val === 'string' ? val : (val?.name || '');
                                    if (name) handleServiceSelect(idx, name);
                                  }}
                                  renderInput={(params) => (
                                    <TextField {...params} placeholder="Type or select a service" variant="standard" />
                                  )}
                                  disableClearable
                                  sx={{ flexGrow: 1 }}
                                />
                                {item.is_previous_due && (
                                  <Chip
                                    label="Previous Due"
                                    size="small"
                                    sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: '#fef3c7', color: '#b45309', borderRadius: 1 }}
                                  />
                                )}
                              </Box>
                              <TextField
                                size="small"
                                variant="standard"
                                fullWidth
                                multiline
                                maxRows={3}
                                placeholder="Note(prints under service)"
                                value={item.print_note || ''}
                                onChange={(e) => updateServiceItem(idx, 'print_note', e.target.value)}
                                sx={{ mt: 0.4 }}
                                inputProps={{ style: { fontSize: 12 } }}
                              />
                              {isIdCardService(item) && (
                                <Box sx={{ mt: 0.5 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => openIdCardConfigModal(idx)}
                                    sx={{
                                      fontSize: 11,
                                      py: 0.2,
                                      px: 1,
                                      minWidth: 0,
                                      textTransform: 'none',
                                      borderColor: '#8b5cf6',
                                      color: '#7c3aed',
                                      fontWeight: 600,
                                      '&:hover': { borderColor: '#7c3aed', bgcolor: '#f5f3ff' },
                                    }}
                                  >
                                    {item.idCardConfig ? 'Edit ID Card Config' : 'Configure ID Cards'}
                                  </Button>
                                  {Array.isArray(item.idCardLines) && item.idCardLines.length > 0 && (
                                    <Box sx={{ mt: 0.5 }}>
                                      {item.idCardLines.map((l, li) => (
                                        <Typography key={li} variant="caption" sx={{ display: 'block', color: '#64748b', fontSize: 10, lineHeight: 1.3 }}>
                                          • {l.label}: {l.count} × ₹{l.unitPrice} = ₹{l.amount}
                                        </Typography>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="text"
                                variant="standard"
                                value={moneyInputValue(item.price)}
                                placeholder={isFixed ? 'Fixed amt' : '0.00'}
                                onChange={(e) => updateServiceItem(idx, 'price', moneyInputValue(e.target.value))}
                                inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                                sx={{ width: 110 }}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <FormControlLabel
                                sx={{ m: 0 }}
                                control={
                                  <Switch
                                    size="small"
                                    checked={isFixed}
                                    onChange={(e) => updateServiceItem(idx, 'is_fixed', e.target.checked)}
                                    color="primary"
                                  />
                                }
                                label={
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: 11,
                                      color: isFixed ? '#2563eb' : '#64748b',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {isFixed ? 'Fixed amount' : 'Per unit'}
                                  </Typography>
                                }
                              />
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                variant="standard"
                                value={moneyInputValue(item.count)}
                                disabled={isFixed}
                                onChange={(e) => updateServiceItem(idx, 'count', moneyInputValue(e.target.value))}
                                inputProps={{ min: 1, step: '1', style: { textAlign: 'right' } }}
                                sx={{ width: 70, opacity: isFixed ? 0.5 : 1 }}
                                helperText={isFixed ? 'Fixed' : ''}
                                FormHelperTextProps={{ sx: { fontSize: 9, textAlign: 'right', m: 0 } }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Switch
                                size="small"
                                checked={!!item.has_gst}
                                onChange={(e) => updateServiceItem(idx, 'has_gst', e.target.checked)}
                                color="primary"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                variant="standard"
                                value={item.gst_percentage}
                                onChange={(e) => updateServiceItem(idx, 'gst_percentage', e.target.value)}
                                disabled={!item.has_gst}
                                inputProps={{ min: 0, max: 100, step: '0.5', style: { textAlign: 'right' } }}
                                sx={{ width: 60, opacity: item.has_gst ? 1 : 0.4 }}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title={item.exclude_from_split ? "Excluded from installment split (amount stays fixed in full)" : "Included in installment split"}>
                                <FormControlLabel
                                  sx={{ m: 0 }}
                                  control={
                                    <Switch
                                      size="small"
                                      checked={!item.exclude_from_split}
                                      onChange={(e) => updateServiceItem(idx, 'exclude_from_split', !e.target.checked)}
                                      color="secondary"
                                    />
                                  }
                                  label={
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: 10,
                                        color: !item.exclude_from_split ? '#059669' : '#dc2626',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {!item.exclude_from_split ? 'Included' : 'Opted Out'}
                                    </Typography>
                                  }
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap' }}>
                              {formatAmount(lineTotal)}
                              {item.exclude_from_split && (
                                <Chip
                                  label="Non-split"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: 9,
                                    fontWeight: 700,
                                    bgcolor: '#ede9fe',
                                    color: '#6d28d9',
                                    border: '1px solid #ddd6fe',
                                    display: 'block',
                                    mt: 0.3,
                                    width: 'fit-content',
                                    ml: 'auto',
                                  }}
                                />
                              )}
                              {isFixed && !item.exclude_from_split && (
                                <Chip
                                  label="Fixed Amount"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: 9,
                                    fontWeight: 700,
                                    bgcolor: '#eff6ff',
                                    color: '#2563eb',
                                    border: '1px solid #bfdbfe',
                                    display: 'block',
                                    mt: 0.3,
                                    width: 'fit-content',
                                    ml: 'auto',
                                  }}
                                />
                              )}
                              {gstAmt > 0 && (
                                <Typography variant="caption" sx={{ display: 'block', color: '#64748b', fontSize: 10 }}>
                                  incl. GST {formatAmount(gstAmt)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                              <Tooltip title={`Print Proforma for ${item.name || 'Service'}`}>
                                <IconButton
                                  size="small"
                                  onClick={() => handlePrintItem(item, idx)}
                                  sx={{
                                    color: '#2563eb',
                                    p: 0.6,
                                    bgcolor: '#eff6ff',
                                    borderRadius: 1.5,
                                    mr: 0.5,
                                    '&:hover': { bgcolor: '#dbeafe' },
                                  }}
                                >
                                  <PrintIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <IconButton size="small" onClick={() => removeServiceItem(idx)} sx={{ color: '#ef4444' }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Totals rows */}
                      {computedGst > 0 && (
                        <>
                          <TableRow sx={{ '& td': { borderTop: '1px solid #e2e8f0', py: 0.8 } }}>
                            <TableCell colSpan={7} align="right" sx={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                              Subtotal (Base)
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
                              {formatAmount(computedSubtotal)}
                            </TableCell>
                            <TableCell />
                          </TableRow>
                          <TableRow sx={{ '& td': { py: 0.8 } }}>
                            <TableCell colSpan={7} align="right" sx={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                              Total GST
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 13, color: '#0284c7', fontWeight: 600 }}>
                              +{formatAmount(computedGst)}
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        </>
                      )}
                      {excludedTotal > 0 && (
                        <>
                          <TableRow sx={{ '& td': { borderTop: '1px solid #e2e8f0', py: 0.6 } }}>
                            <TableCell colSpan={7} align="right" sx={{ fontSize: 12, color: '#2563eb', fontWeight: 600 }}>
                              Splittable Services Amount
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 13, color: '#2563eb', fontWeight: 700 }}>
                              {formatAmount(splittableTotal)}
                            </TableCell>
                            <TableCell />
                          </TableRow>
                          <TableRow sx={{ '& td': { py: 0.6 } }}>
                            <TableCell colSpan={7} align="right" sx={{ fontSize: 12, color: '#6d28d9', fontWeight: 600 }}>
                              Non-split / Opted-out Services
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 13, color: '#6d28d9', fontWeight: 700 }}>
                              {formatAmount(excludedTotal)}
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        </>
                      )}
                      <TableRow sx={{ bgcolor: '#f8fafc', '& td': { borderTop: '2px solid #cbd5e1', py: 1.2 } }}>
                        <TableCell colSpan={7} align="right" sx={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>
                          Grand Total
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, fontSize: 15, color: '#2563eb' }}>
                          {formatAmount(computedTotal)}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => addServiceItem(false)}
                    sx={{ textTransform: 'none', fontWeight: 600, color: '#2563eb' }}
                  >
                    Add Another Service
                  </Button>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => addServiceItem(true)}
                    sx={{ textTransform: 'none', fontWeight: 600, color: '#2563eb' }}
                  >
                    Add Fixed Amount Item
                  </Button>
                </Box>
              </>
            )}
          </Paper>

          {/* Installments Section */}
          <Paper sx={sectionStyle}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography sx={sectionTitleStyle}>
                <SplitscreenIcon sx={{ fontSize: 18 }} /> Installments
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  type="number"
                  placeholder="Split count..."
                  value={splitCount}
                  onChange={(e) => setSplitCount(e.target.value)}
                  sx={{ width: 120 }}
                  inputProps={{ min: 1 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSplitInstallments}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
                >
                  Split Evenly
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addNewInstallmentRow}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
                >
                  Add Row
                </Button>
                <Tooltip title={selectedInstallmentKeys.length > 0 ? `Print Proforma for ${selectedInstallmentKeys.length} selected` : 'Print Proforma (same as top — all items)'}>
                  <IconButton
                    size="small"
                    onClick={handlePrintAllInstallments}
                    sx={{
                      color: '#2563eb',
                      p: 0.8,
                      bgcolor: '#eff6ff',
                      border: '1px solid #bfdbfe',
                      borderRadius: 1.5,
                      '&:hover': { bgcolor: '#dbeafe' },
                    }}
                  >
                    <PrintIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            {excludedTotal > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f5f3ff', border: '1px solid #ddd6fe', p: 1.5, borderRadius: 2, mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="body2" sx={{ color: '#5b21b6', fontWeight: 600 }}>
                  <strong>Splittable Installment Pool:</strong> {formatAmount(splittableTotal)}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6d28d9', fontWeight: 700 }}>
                  Non-Split Services (Fixed in Full): {formatAmount(excludedTotal)}
                </Typography>
              </Box>
            )}

            <TableContainer sx={{ overflowX: 'auto', width: '100%', borderRadius: 2, border: '1px solid #e2e8f0', mb: 2 }}>
              <Table size="small" sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc', '& th': { fontWeight: 700, fontSize: 11, color: '#475569', textTransform: 'uppercase', py: 1.2 } }}>
                    <TableCell padding="checkbox" sx={{ width: 42 }}>
                      <Checkbox
                        size="small"
                        checked={allInstallmentRowsSelected}
                        indeterminate={selectedInstallmentKeys.length > 0 && !allInstallmentRowsSelected}
                        onChange={toggleAllInstallmentRows}
                        disabled={installmentSelectableKeys.length === 0}
                        inputProps={{ 'aria-label': 'Select all installment rows' }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ width: 44 }}>Print</TableCell>
                    <TableCell sx={{ width: 40 }}>#</TableCell>
                    <TableCell sx={{ minWidth: 140 }}>Label</TableCell>
                    <TableCell sx={{ minWidth: 130 }}>Due Date</TableCell>
                    <TableCell align="right" sx={{ width: 95 }}>%</TableCell>
                    <TableCell align="right" sx={{ width: 125 }}>Amount</TableCell>
                    <TableCell align="right" sx={{ width: 90 }}>Paid</TableCell>
                    <TableCell align="right" sx={{ width: 90 }}>Pending</TableCell>
                    <TableCell align="center" sx={{ width: 95 }}>Status</TableCell>
                    <TableCell align="center" sx={{ width: 50 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {installments.map((inst, idx) => {
                    if (isEmptyInstallment(inst)) return null;
                    const instAmt = parseFloat(inst.amount || 0);
                    const paidAmt = parseFloat(inst.paid_amount || 0);
                    const pendingAmt = Math.max(0, instAmt - paidAmt);
                    const isPaid = inst.is_paid || (instAmt > 0 && paidAmt >= instAmt);
                    const isPartial = !isPaid && paidAmt > 0;
                    const isLocked = isPaid || paidAmt > 0;
                    const rowKey = installmentRowKey(inst, idx);
                    const isRowSelected = selectedInstallmentKeys.includes(rowKey);

                    return (
                      <TableRow key={inst.id || `inst-row-${idx}`} sx={{ '&:hover': { bgcolor: '#f8fafc' }, bgcolor: isRowSelected ? '#eff6ff' : undefined }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={isRowSelected}
                            disabled={!inst.id}
                            onChange={() => toggleInstallmentRow(rowKey)}
                            inputProps={{ 'aria-label': `Select ${inst.label || `Installment ${idx + 1}`}` }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ width: 44, py: 0.5 }}>
                          {inst.id ? (
                            <Tooltip title={`Print Proforma for ${inst.label || `Installment ${idx + 1}`}`}>
                              <IconButton
                                size="small"
                                onClick={() => handlePrintInstallment(inst)}
                                sx={{
                                  color: '#2563eb',
                                  p: 0.6,
                                  bgcolor: '#eff6ff',
                                  borderRadius: 1.5,
                                  '&:hover': { bgcolor: '#dbeafe' },
                                }}
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>-</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>
                          {inst.installment_number || idx + 1}
                        </TableCell>
                        <TableCell>
                          {isLocked ? (
                            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{inst.label}</Typography>
                          ) : (
                            <TextField
                              size="small"
                              value={inst.label || ''}
                              placeholder={`Installment ${idx + 1}`}
                              onChange={(e) => updateInstallment(idx, 'label', e.target.value)}
                              sx={{
                                width: '100%',
                                minWidth: 120,
                                '& .MuiOutlinedInput-input': { py: 0.6, px: 1, fontSize: 13 },
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {isLocked ? (
                            <Typography sx={{ fontSize: 13 }}>{inst.due_date || '-'}</Typography>
                          ) : (
                            <TextField
                              size="small"
                              type="date"
                              value={inst.due_date || ''}
                              onChange={(e) => updateInstallment(idx, 'due_date', e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              sx={{
                                width: '100%',
                                minWidth: 130,
                                '& .MuiOutlinedInput-input': { py: 0.6, px: 1, fontSize: 12 },
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {isLocked ? (
                            <Typography sx={{ fontWeight: 600, color: '#475569', fontSize: 13 }}>
                              {inst.percentage
                                ? `${parseFloat(inst.percentage).toFixed(2)}%`
                                : targetSplitPool > 0
                                  ? `${((instAmt / targetSplitPool) * 100).toFixed(2)}%`
                                  : '-'}
                            </Typography>
                          ) : (
                            <TextField
                              size="small"
                              type="number"
                              placeholder="%"
                              value={inst.percentage ?? ''}
                              onChange={(e) => updateInstallment(idx, 'percentage', e.target.value)}
                              InputProps={{
                                endAdornment: <InputAdornment position="end" sx={{ '& p': { fontSize: 11 } }}>%</InputAdornment>,
                              }}
                              inputProps={{ min: 0, max: 100, step: '0.01' }}
                              sx={{
                                width: 85,
                                '& .MuiOutlinedInput-input': { py: 0.6, px: 0.8, fontSize: 13, textAlign: 'right' },
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {isLocked ? (
                            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{formatAmount(inst.amount)}</Typography>
                          ) : (
                            <TextField
                              size="small"
                              type="number"
                              placeholder="Amount"
                              value={inst.amount ?? ''}
                              onChange={(e) => updateInstallment(idx, 'amount', e.target.value)}
                              InputProps={{
                                startAdornment: <InputAdornment position="start" sx={{ '& p': { fontSize: 11 } }}>₹</InputAdornment>,
                              }}
                              inputProps={{ min: 0, step: '0.01' }}
                              sx={{
                                width: 115,
                                '& .MuiOutlinedInput-input': { py: 0.6, px: 0.8, fontSize: 13, textAlign: 'right' },
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#16a34a', fontSize: 13 }}>
                          {formatAmount(paidAmt)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: pendingAmt > 0 ? '#dc2626' : '#16a34a', fontSize: 13 }}>
                          {formatAmount(pendingAmt)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={isPaid ? 'Paid' : isPartial ? 'Partially Paid' : 'Pending'}
                            size="small"
                            sx={{
                              background: isPaid ? '#d1fae5' : isPartial ? '#fef3c7' : '#f3f4f6',
                              color: isPaid ? '#059669' : isPartial ? '#d97706' : '#64748b',
                              fontWeight: 700,
                              fontSize: 11,
                              borderRadius: 1.5,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ width: 50 }}>
                          {isLocked ? (
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>-</Typography>
                          ) : (
                            <Tooltip title="Remove installment">
                              <IconButton
                                size="small"
                                onClick={() => removeInstallment(idx)}
                                sx={{ color: '#ef4444', p: 0.6, '&:hover': { bgcolor: '#fee2e2' } }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {excludedItemsList.map((exItem, eIdx) => {
                    const exKey = excludedRowKey(exItem, eIdx);
                    const isExSelected = selectedInstallmentKeys.includes(exKey);
                    const linkedPayable = payableInstallmentItems.find(
                      (it) => !it.id && Number(it.itemIndex) === Number(exItem.originalIndex ?? eIdx)
                    );
                    const exPaid = linkedPayable ? linkedPayable.paidAmount : 0;
                    const exPending = linkedPayable
                      ? linkedPayable.pendingAmount
                      : parseFloat(exItem.lineTotal || 0);
                    const exFullyPaid = Boolean(linkedPayable?.isPaid);
                    const exPartial = !exFullyPaid && exPaid > 0;
                    return (
                    <TableRow key={`ex-row-${eIdx}`} sx={{ bgcolor: isExSelected ? '#ede9fe' : '#faf5ff', '&:hover': { bgcolor: '#f3e8ff' } }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={isExSelected}
                          onChange={() => toggleInstallmentRow(exKey)}
                          inputProps={{ 'aria-label': `Select ${exItem.name || 'non-split item'}` }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ width: 44 }}>
                        <Tooltip title={`Print Proforma for ${exItem.name || 'Item'}`}>
                          <IconButton
                            size="small"
                            onClick={() => handlePrintItem(exItem, exItem.originalIndex !== undefined ? exItem.originalIndex : eIdx, true)}
                            sx={{
                              color: '#6d28d9',
                              p: 0.6,
                              bgcolor: '#f5f3ff',
                              borderRadius: 1.5,
                              '&:hover': { bgcolor: '#ede9fe' },
                            }}
                          >
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell><Chip label="Non-split" size="small" sx={{ bgcolor: '#ede9fe', color: '#6d28d9', fontWeight: 700, height: 20, fontSize: 10 }} /></TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#4c1d95' }}>
                        {exItem.name || 'Service'}
                        <Typography variant="caption" sx={{ display: 'block', color: '#6d28d9', fontSize: 10 }}>
                          Fixed amount / Excluded from split
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: '#64748b' }}>Immediate / On Order</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#6d28d9' }}>100%</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#4c1d95' }}>{formatAmount(exItem.lineTotal)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#16a34a', fontSize: 13 }}>
                        {formatAmount(exPaid)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: exPending > 0 ? '#dc2626' : '#16a34a', fontSize: 13 }}>
                        {formatAmount(exPending)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={exFullyPaid ? 'Paid' : exPartial ? 'Partially Paid' : 'Pay in Full'}
                          size="small"
                          sx={{
                            bgcolor: exFullyPaid ? '#d1fae5' : exPartial ? '#fef3c7' : '#ede9fe',
                            color: exFullyPaid ? '#059669' : exPartial ? '#d97706' : '#6d28d9',
                            fontWeight: 700,
                            fontSize: 10,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>-</Typography>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                  {visibleInstallmentCount === 0 && excludedItemsList.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 3, color: '#94a3b8' }}>
                        No installments configured. Click "Add Row" or enter a split count and click "Split Evenly" above.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {visibleInstallmentCount > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 1.5,
                  mb: 1.5,
                  borderRadius: 2,
                  bgcolor: installmentPctMatched ? '#f0fdf4' : '#fffbeb',
                  border: '1px solid',
                  borderColor: installmentPctMatched ? '#bbf7d0' : '#fde68a',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: installmentPctMatched ? '#166534' : '#92400e' }}>
                  {receivedAmount > 0 ? 'Remaining split %: ' : 'Total Percentage: '}{totalInstallmentPercentage.toFixed(2)}%
                  {installmentPctMatched ? ' ✓ (100% matched)' : ' (Must equal 100%)'}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                  Total: {formatAmount(totalInstallmentAmount)}
                </Typography>
              </Box>
            )}

            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addNewInstallmentRow}
                sx={{ textTransform: 'none', fontWeight: 600, color: '#2563eb', borderColor: '#bfdbfe', borderRadius: 1.5 }}
              >
                Add Row
              </Button>
              {visibleInstallmentCount > 0 && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSaveCustomInstallments}
                  disabled={!getActiveOrderId()}
                  sx={{
                    textTransform: 'none', fontWeight: 600, borderRadius: 1.5,
                    background: '#2563eb',
                    '&:hover': { background: '#1d4ed8' },
                  }}
                >
                  Save Installments
                </Button>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Right Column - Payments */}
        <Grid item xs={12} lg={5} xl={4.5}>
          {/* Add Payment */}
          <Paper sx={sectionStyle}>
            <Typography sx={sectionTitleStyle}>
              <PaymentIcon sx={{ fontSize: 18 }} /> Add Payment
            </Typography>
            <Grid container spacing={1.5}>
              {payableInstallmentItems.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', fontSize: 11, display: 'block', mb: 0.8 }}>
                    Installment Items
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 200, overflowX: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc', '& th': { fontWeight: 700, fontSize: 11, color: '#475569', textTransform: 'uppercase', py: 0.8, bgcolor: '#f8fafc' } }}>
                          <TableCell align="center" sx={{ width: 36, py: 0.5 }}>
                            <Checkbox
                              size="small"
                              disabled={selectablePayableItems.length === 0}
                              checked={selectablePayableItems.length > 0 && selectablePayableItems.every((it) => sideItemPayments[it.key]?.selected)}
                              indeterminate={
                                selectablePayableItems.some((it) => sideItemPayments[it.key]?.selected) &&
                                !selectablePayableItems.every((it) => sideItemPayments[it.key]?.selected)
                              }
                              onChange={handleToggleAllSidePayableItems}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: 11 }}>Label</TableCell>
                          <TableCell align="right" sx={{ fontSize: 11 }}>Total</TableCell>
                          <TableCell align="right" sx={{ width: 120, fontSize: 11 }}>Enter Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {payableInstallmentItems.map((item) => {
                          const isSelected = Boolean(sideItemPayments[item.key]?.selected);
                          const enteredAmt = sideItemPayments[item.key]?.amount || '';
                          const isAlreadyReceived = item.isPaid || item.pendingAmount <= 0;
                          return (
                            <TableRow
                              key={item.key}
                              sx={{
                                bgcolor: isAlreadyReceived ? '#f8fafc' : isSelected ? '#f0fdf4' : 'inherit',
                                opacity: isAlreadyReceived ? 0.75 : 1,
                                '&:hover': { bgcolor: isAlreadyReceived ? '#f8fafc' : isSelected ? '#dcfce7' : '#f8fafc' },
                              }}
                            >
                              <TableCell align="center" sx={{ width: 36, py: 0.6 }}>
                                <Checkbox
                                  size="small"
                                  disabled={isAlreadyReceived}
                                  checked={isAlreadyReceived ? false : isSelected}
                                  onChange={(e) => handleToggleSidePayableItem(item, e.target.checked)}
                                />
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: 11, py: 0.6 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                  <span>{item.label}</span>
                                  {isAlreadyReceived && (
                                    <Chip
                                      label="Already Received"
                                      size="small"
                                      sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700, height: 16, fontSize: 8.5 }}
                                    />
                                  )}
                                </Box>
                                {item.paidAmount > 0 && !isAlreadyReceived && (
                                  <Typography variant="caption" sx={{ display: 'block', color: '#d97706', fontSize: 9.5 }}>
                                    Pending: {formatAmount(item.pendingAmount)}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 11, py: 0.6 }}>
                                {formatAmount(item.totalAmount)}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 0.6 }}>
                                {isAlreadyReceived ? (
                                  <Typography variant="caption" sx={{ color: '#16a34a', fontWeight: 700, pr: 0.5, fontSize: 10 }}>
                                    {formatAmount(item.paidAmount || item.totalAmount)} Received
                                  </Typography>
                                ) : (
                                  <TextField
                                    size="small"
                                    type="number"
                                    placeholder="0.00"
                                    disabled={isAlreadyReceived}
                                    value={enteredAmt}
                                    onChange={(e) => handleSideItemAmountChange(item, e.target.value)}
                                    onBlur={() => handleSideItemAmountBlur(item)}
                                    inputProps={{
                                      min: 0,
                                      max: balanceAmount > 0 ? balanceAmount : computedTotal,
                                      step: '0.01',
                                      style: { textAlign: 'right', fontSize: 11, padding: '3px 6px' },
                                    }}
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start" sx={{ '& p': { fontSize: 10 } }}>₹</InputAdornment>,
                                    }}
                                    sx={{ width: 105 }}
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  label="Payment Amount (₹)"
                  type="number"
                  value={paymentForm.amount}
                  error={Boolean(paymentForm.amount && parseFloat(paymentForm.amount) > (balanceAmount > 0 ? balanceAmount : computedTotal))}
                  onChange={(e) => {
                    const val = e.target.value;
                    const num = parseFloat(val);
                    const maxPayable = balanceAmount > 0 ? balanceAmount : computedTotal;
                    if (!isNaN(num) && maxPayable > 0 && num > maxPayable) {
                      setPaymentForm({ ...paymentForm, amount: maxPayable.toFixed(2) });
                    } else {
                      setPaymentForm({ ...paymentForm, amount: val });
                    }
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  inputProps={{
                    min: 0.01,
                    max: balanceAmount > 0 ? balanceAmount : computedTotal,
                    step: '0.01',
                  }}
                  helperText={
                    Boolean(paymentForm.amount && parseFloat(paymentForm.amount) > (balanceAmount > 0 ? balanceAmount : computedTotal))
                      ? `Amount cannot exceed remaining balance of ₹${(balanceAmount > 0 ? balanceAmount : computedTotal).toFixed(2)}`
                      : balanceAmount > 0
                        ? `Remaining balance: ₹${balanceAmount.toFixed(2)}`
                        : ''
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  label="Payment Received Date"
                  type="datetime-local"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Method</InputLabel>
                  <Select
                    value={paymentForm.method}
                    label="Method"
                    onChange={(e) => setPaymentForm({
                      ...paymentForm,
                      method: e.target.value,
                      cheque_status: e.target.value === 'cheque' ? (paymentForm.cheque_status || 'received') : 'received',
                    })}
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {paymentForm.method === 'cheque' && (
                <Grid item xs={12}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Cheque Status</InputLabel>
                    <Select
                      value={paymentForm.cheque_status || 'received'}
                      label="Cheque Status"
                      onChange={(e) => setPaymentForm({ ...paymentForm, cheque_status: e.target.value })}
                    >
                      {CHEQUE_STATUSES.map((s) => (
                        <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  label="Reference"
                  value={paymentForm.reference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  label="Notes"
                  multiline
                  rows={2}
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<PaymentIcon />}
                  onClick={handleAddPayment}
                  disabled={
                    paymentSubmitting ||
                    !getActiveOrderId() ||
                    !paymentForm.amount ||
                    Boolean(paymentForm.amount && parseFloat(paymentForm.amount) > (balanceAmount > 0 ? balanceAmount : computedTotal))
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    background: '#059669',
                    '&:hover': { background: '#047857' },
                  }}
                >
                  {paymentSubmitting ? 'Adding...' : 'Add Payment'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Payment History */}
          <Paper sx={sectionStyle}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography sx={sectionTitleStyle}>
                💰 Payment History
                <Chip
                  label={`${payments.length} payment${payments.length !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{ ml: 1, background: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: 11 }}
                />
              </Typography>
              {payments.length > 0 && (
                <Tooltip title="Print all payment receipts">
                  <IconButton
                    size="small"
                    onClick={handlePrintAllPayments}
                    sx={{
                      color: '#059669',
                      p: 0.8,
                      bgcolor: '#ecfdf5',
                      border: '1px solid #a7f3d0',
                      borderRadius: 1.5,
                      '&:hover': { bgcolor: '#d1fae5' },
                    }}
                  >
                    <PrintIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {payments.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#94a3b8', textAlign: 'center', py: 2 }}>
                No payments yet
              </Typography>
            ) : (
              <TableContainer sx={{ overflowX: 'auto', width: '100%', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc', '& th': { fontWeight: 700, fontSize: 11, color: '#475569', textTransform: 'uppercase', py: 1.2 } }}>
                      <TableCell align="center" sx={{ width: 44 }}>Print</TableCell>
                      <TableCell>Payment Received Date</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Cheque Status</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="center" sx={{ width: 44 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((pay) => (
                      <TableRow key={pay.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                        <TableCell align="center" sx={{ width: 44, py: 0.5 }}>
                          <Tooltip title="Print payment receipt">
                            <IconButton
                              size="small"
                              onClick={() => handlePrintPayment(pay)}
                              sx={{
                                color: '#059669',
                                p: 0.6,
                                bgcolor: '#ecfdf5',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#d1fae5' },
                              }}
                            >
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {pay.payment_date ? new Date(pay.payment_date).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={PAYMENT_METHODS.find(m => m.value === pay.method)?.label || pay.method}
                            size="small"
                            sx={{ fontWeight: 600, fontSize: 10, background: '#f1f5f9', color: '#475569', borderRadius: 1.5 }}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 130 }}>
                          {pay.method === 'cheque' ? (
                            <FormControl size="small" fullWidth>
                              <Select
                                value={pay.cheque_status || 'received'}
                                onChange={(e) => handleUpdateChequeStatus(pay, e.target.value)}
                                disabled={updatingChequeStatusId === pay.id}
                                sx={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  height: 28,
                                  borderRadius: 1.5,
                                  bgcolor: (CHEQUE_STATUS_COLORS[pay.cheque_status || 'received'] || {}).bg,
                                  color: (CHEQUE_STATUS_COLORS[pay.cheque_status || 'received'] || {}).color,
                                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                }}
                              >
                                {CHEQUE_STATUSES.map((s) => (
                                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#16a34a' }}>
                          {formatAmount(pay.amount)}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Delete collected payment">
                            <IconButton
                              size="small"
                              onClick={() => handleDeletePayment(pay)}
                              disabled={deletingPaymentId === pay.id}
                              sx={{ color: '#ef4444', p: 0.5, '&:hover': { bgcolor: '#fee2e2' } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: '#f8fafc', '& td': { borderTop: '2px solid #e2e8f0', py: 1 } }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>Total Received</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, fontSize: 14, color: '#16a34a' }}>
                        {formatAmount(receivedAmount)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Received amount locked notice */}
            <Alert
              severity="info"
              icon={<LockIcon fontSize="small" />}
              sx={{ mt: 2, borderRadius: 2, '& .MuiAlert-message': { fontSize: 12 } }}
            >
              Received amount is <strong>locked</strong> — it's the sum of all payments and cannot be edited manually.
              Changing services or prices will adjust the balance, not the received amount.
            </Alert>
          </Paper>
        </Grid>
      </Grid>

      {/* Sticky Bottom Action Bar with 3 Separate Buttons */}
      <Paper
        elevation={4}
        sx={{
          mt: 4,
          p: { xs: 1.5, sm: 2 },
          borderRadius: 3,
          background: 'rgba(255, 255, 255, 0.94)',
          backdropFilter: 'blur(16px)',
          border: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          position: 'sticky',
          bottom: 16,
          zIndex: 10,
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'space-around', sm: 'flex-start' },
          gap: { xs: 1.5, sm: 3 },
          width: { xs: '100%', md: 'auto' },
          flexWrap: 'wrap',
        }}>
          <Box>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontWeight: 700, fontSize: 10, letterSpacing: 0.5 }}>TOTAL AMOUNT</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b', fontSize: { xs: 15, sm: 18 } }}>{formatAmount(computedTotal)}</Typography>
          </Box>
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
          <Box>
            <Typography variant="caption" sx={{ color: '#047857', display: 'block', fontWeight: 700, fontSize: 10, letterSpacing: 0.5 }}>RECEIVED</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#16a34a', fontSize: { xs: 15, sm: 18 } }}>{formatAmount(receivedAmount)}</Typography>
          </Box>
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
          <Box>
            <Typography variant="caption" sx={{ color: balanceAmount > 0 ? '#b91c1c' : '#047857', display: 'block', fontWeight: 700, fontSize: 10, letterSpacing: 0.5 }}>BALANCE</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: balanceAmount > 0 ? '#dc2626' : '#16a34a', fontSize: { xs: 15, sm: 18 } }}>
              {formatAmount(balanceAmount)}
            </Typography>
          </Box>
        </Box>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ width: { xs: '100%', md: 'auto' }, flexWrap: 'wrap' }}
        >
          {/* Button 1: Print Invoice */}
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => handlePrintInvoice('proforma')}
            disabled={printingInvoice}
            sx={{
              flex: { xs: 1, sm: 'none' },
              borderColor: '#2563eb',
              color: '#2563eb',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 14,
              borderRadius: 2,
              px: 2.5,
              py: 1,
              whiteSpace: 'nowrap',
              '&:hover': { bgcolor: '#eff6ff', borderColor: '#1d4ed8' },
            }}
          >
            {printingInvoice ? 'Printing...' : 'Print Proforma'}
          </Button>

          <Button
            variant="outlined"
            startIcon={<ReceiptIcon />}
            onClick={() => handlePrintInvoice('pending')}
            disabled={printingPending}
            sx={{
              flex: { xs: 1, sm: 'none' },
              borderColor: '#dc2626',
              color: '#dc2626',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 14,
              borderRadius: 2,
              px: 2.5,
              py: 1,
              whiteSpace: 'nowrap',
              '&:hover': { bgcolor: '#fef2f2', borderColor: '#b91c1c' },
            }}
          >
            {printingPending ? 'Printing...' : 'Pending Invoice'}
          </Button>

          {/* Button 2: Add Amount */}
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenAddAmountModal}
            sx={{
              flex: { xs: 1, sm: 'none' },
              background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 14,
              borderRadius: 2,
              px: 3,
              py: 1,
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
              '&:hover': { background: 'linear-gradient(135deg, #047857 0%, #059669 100%)' },
            }}
          >
            Add Amount
          </Button>

          {/* Button 3: Save Order */}
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => handleSave()}
            disabled={saving}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 14,
              borderRadius: 2,
              px: 3,
              py: 1,
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
              '&:hover': { background: 'linear-gradient(135deg, #1d4ed8 0%, #6d28d9 100%)' },
            }}
          >
            {saving ? 'Saving...' : 'Save Order'}
          </Button>
        </Stack>
      </Paper>

      {/* Dedicated Add Amount Dialog */}
      <Dialog
        open={addAmountModalOpen}
        onClose={() => !modalPaymentSubmitting && setAddAmountModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon sx={{ color: '#059669' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Add Amount (Record Payment)</Typography>
          </Box>
          {orderData?.order_number && (
            <Chip label={orderData.order_number} size="small" sx={{ bgcolor: '#e0e7ff', color: '#3730a3', fontWeight: 600 }} />
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {/* Quick Balance Summary */}
          <Paper sx={{ p: 2, mb: 2.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b' }}>Total Order</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatAmount(computedTotal)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b' }}>Received So Far</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#16a34a' }}>{formatAmount(receivedAmount)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b' }}>Outstanding Balance</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: balanceAmount > 0 ? '#dc2626' : '#16a34a' }}>
                {formatAmount(balanceAmount)}
              </Typography>
            </Box>
          </Paper>

          {/* Existing Collected Amounts */}
          {payments && payments.length > 0 && (
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', fontSize: 11, display: 'block', mb: 0.8 }}>
                Collected Amounts ({payments.length})
              </Typography>
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Table size="small">
                  <TableBody>
                    {payments.map((p) => (
                      <TableRow key={p.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                        <TableCell sx={{ fontSize: 11, py: 0.8 }}>
                          {p.payment_date ? new Date(p.payment_date).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11, py: 0.8, color: '#64748b' }}>
                          {PAYMENT_METHODS.find(m => m.value === p.method)?.label || p.method}
                          {p.method === 'cheque' && (
                            <Chip
                              label={CHEQUE_STATUSES.find((s) => s.value === (p.cheque_status || 'received'))?.label || p.cheque_status}
                              size="small"
                              sx={{
                                ml: 0.75,
                                height: 18,
                                fontSize: 9,
                                fontWeight: 700,
                                bgcolor: (CHEQUE_STATUS_COLORS[p.cheque_status || 'received'] || {}).bg,
                                color: (CHEQUE_STATUS_COLORS[p.cheque_status || 'received'] || {}).color,
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#16a34a', fontSize: 12, py: 0.8 }}>
                          {formatAmount(p.amount)}
                        </TableCell>
                        <TableCell align="center" sx={{ width: 36, py: 0.8 }}>
                          <Tooltip title="Delete collected amount">
                            <IconButton
                              size="small"
                              onClick={() => handleDeletePayment(p)}
                              disabled={deletingPaymentId === p.id}
                              sx={{ color: '#ef4444', p: 0.3, '&:hover': { bgcolor: '#fee2e2' } }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          )}

          <Stack spacing={2.5}>
            {payableInstallmentItems.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', fontSize: 11, display: 'block', mb: 0.8 }}>
                  Installment Items
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 220, overflowX: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc', '& th': { fontWeight: 700, fontSize: 11, color: '#475569', textTransform: 'uppercase', py: 1, bgcolor: '#f8fafc' } }}>
                        <TableCell align="center" sx={{ width: 44, py: 0.5 }}>
                          <Checkbox
                            size="small"
                            disabled={selectablePayableItems.length === 0}
                            checked={selectablePayableItems.length > 0 && selectablePayableItems.every((it) => itemPayments[it.key]?.selected)}
                            indeterminate={
                              selectablePayableItems.some((it) => itemPayments[it.key]?.selected) &&
                              !selectablePayableItems.every((it) => itemPayments[it.key]?.selected)
                            }
                            onChange={handleToggleAllPayableItems}
                          />
                        </TableCell>
                        <TableCell>Label</TableCell>
                        <TableCell align="right">Total Amount</TableCell>
                        <TableCell align="right" sx={{ width: 140 }}>Enter Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payableInstallmentItems.map((item) => {
                        const isSelected = Boolean(itemPayments[item.key]?.selected);
                        const enteredAmt = itemPayments[item.key]?.amount || '';
                        const isAlreadyReceived = item.isPaid || item.pendingAmount <= 0;
                        return (
                          <TableRow
                            key={item.key}
                            sx={{
                              bgcolor: isAlreadyReceived ? '#f8fafc' : isSelected ? '#f0fdf4' : 'inherit',
                              opacity: isAlreadyReceived ? 0.75 : 1,
                              '&:hover': { bgcolor: isAlreadyReceived ? '#f8fafc' : isSelected ? '#dcfce7' : '#f8fafc' },
                            }}
                          >
                            <TableCell align="center" sx={{ width: 44, py: 0.8 }}>
                              <Checkbox
                                size="small"
                                disabled={isAlreadyReceived}
                                checked={isAlreadyReceived ? false : isSelected}
                                onChange={(e) => handleTogglePayableItem(item, e.target.checked)}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.8 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                <span>{item.label}</span>
                                {isAlreadyReceived && (
                                  <Chip
                                    label="Already Received"
                                    size="small"
                                    sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700, height: 18, fontSize: 9 }}
                                  />
                                )}
                              </Box>
                              {item.paidAmount > 0 && !isAlreadyReceived && (
                                <Typography variant="caption" sx={{ display: 'block', color: '#d97706', fontSize: 10 }}>
                                  Pending: {formatAmount(item.pendingAmount)} (Paid: {formatAmount(item.paidAmount)})
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12, py: 0.8 }}>
                              {formatAmount(item.totalAmount)}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 0.8 }}>
                              {isAlreadyReceived ? (
                                <Typography variant="caption" sx={{ color: '#16a34a', fontWeight: 700, pr: 1 }}>
                                  {formatAmount(item.paidAmount || item.totalAmount)} Received
                                </Typography>
                              ) : (
                                <TextField
                                  size="small"
                                  type="number"
                                  placeholder="0.00"
                                  disabled={isAlreadyReceived}
                                  value={enteredAmt}
                                  onChange={(e) => handleItemAmountChange(item, e.target.value)}
                                  onBlur={() => handleItemAmountBlur(item)}
                                  inputProps={{
                                    min: 0,
                                    max: balanceAmount > 0 ? balanceAmount : computedTotal,
                                    step: '0.01',
                                    style: { textAlign: 'right', fontSize: 12, padding: '4px 8px' },
                                  }}
                                  InputProps={{
                                    startAdornment: <InputAdornment position="start" sx={{ '& p': { fontSize: 11 } }}>₹</InputAdornment>,
                                  }}
                                  sx={{ width: 120 }}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            <TextField
              label="Payment Amount (₹)"
              type="number"
              fullWidth
              size="small"
              value={modalPayment.amount}
              error={Boolean(modalPayment.amount && parseFloat(modalPayment.amount) > (balanceAmount > 0 ? balanceAmount : computedTotal))}
              onChange={(e) => {
                const val = e.target.value;
                const num = parseFloat(val);
                const maxPayable = balanceAmount > 0 ? balanceAmount : computedTotal;
                if (!isNaN(num) && maxPayable > 0 && num > maxPayable) {
                  setModalPayment({ ...modalPayment, amount: maxPayable.toFixed(2) });
                } else {
                  setModalPayment({ ...modalPayment, amount: val });
                }
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
              inputProps={{
                min: 0.01,
                max: balanceAmount > 0 ? balanceAmount : computedTotal,
                step: '0.01',
              }}
              helperText={
                Boolean(modalPayment.amount && parseFloat(modalPayment.amount) > (balanceAmount > 0 ? balanceAmount : computedTotal))
                  ? `Amount cannot exceed remaining balance of ₹${(balanceAmount > 0 ? balanceAmount : computedTotal).toFixed(2)}`
                  : balanceAmount > 0
                    ? `Remaining balance: ₹${balanceAmount.toFixed(2)}`
                    : ''
              }
              autoFocus
            />

            <TextField
              label="Payment Received Date & Time"
              type="datetime-local"
              fullWidth
              size="small"
              value={modalPayment.payment_date}
              onChange={(e) => setModalPayment({ ...modalPayment, payment_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />

            <FormControl fullWidth size="small">
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={modalPayment.method}
                label="Payment Method"
                onChange={(e) => setModalPayment({
                  ...modalPayment,
                  method: e.target.value,
                  cheque_status: e.target.value === 'cheque' ? (modalPayment.cheque_status || 'received') : 'received',
                })}
              >
                {PAYMENT_METHODS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {modalPayment.method === 'cheque' && (
              <FormControl fullWidth size="small">
                <InputLabel>Cheque Status</InputLabel>
                <Select
                  value={modalPayment.cheque_status || 'received'}
                  label="Cheque Status"
                  onChange={(e) => setModalPayment({ ...modalPayment, cheque_status: e.target.value })}
                >
                  {CHEQUE_STATUSES.map((s) => (
                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Reference / Transaction ID (Optional)"
              fullWidth
              size="small"
              placeholder="e.g. UTR12345678, CHQ-0092"
              value={modalPayment.reference}
              onChange={(e) => setModalPayment({ ...modalPayment, reference: e.target.value })}
            />

            <TextField
              label="Notes (Optional)"
              fullWidth
              multiline
              rows={2}
              size="small"
              placeholder="Payment remarks or bank notes..."
              value={modalPayment.notes}
              onChange={(e) => setModalPayment({ ...modalPayment, notes: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setAddAmountModalOpen(false)}
            disabled={modalPaymentSubmitting}
            sx={{ textTransform: 'none', color: '#64748b' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleModalPaymentSubmit}
            disabled={
              modalPaymentSubmitting ||
              !modalPayment.amount ||
              Boolean(modalPayment.amount && parseFloat(modalPayment.amount) > (balanceAmount > 0 ? balanceAmount : computedTotal))
            }
            startIcon={modalPaymentSubmitting ? <CircularProgress size={16} color="inherit" /> : <PaymentIcon />}
            sx={{
              background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
            }}
          >
            {modalPaymentSubmitting ? 'Recording...' : 'Add Amount'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ID Card Configuration Modal */}
      <IdCardServiceConfigModal
        open={idCardModalOpen}
        onClose={() => {
          setIdCardModalOpen(false);
          setIdCardEditIndex(null);
          setIdCardInitialConfig(null);
        }}
        onConfirm={handleIdCardConfigConfirm}
        initialConfig={idCardInitialConfig}
      />
    </Box>
  );
}

export default OrdersV2Form;
